import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import toast, { Toaster } from 'react-hot-toast';
import SocioSearch from './SocioSearch';
import API_BASE_URL from '../Config';
import SocioServiciosSection from './socio-components/SocioServiciosSection';
import { useAuth } from '../contexts/AuthContext';
import {
    Calculator, Save, AlertTriangle, FileText, CheckCircle,
    User, Shield, Wallet, Briefcase, TrendingUp, AlertCircle, Clock, Info,
    ChevronDown, ChevronRight, ChevronLeft, MapPin, Plus, Trash2, Upload, Camera, X, ExternalLink,
    Phone, Activity, ShieldCheck, RefreshCw
} from 'lucide-react';

const Field = ({ label, children, className = "" }) => (
    <div className={`enterprise-field ${className}`}>
        {children}
        <label>{label}</label>
    </div>
);

const Perfil360Sidebar = ({ socio, perfil360, simulacion, antiguedadSocio }) => {
    if (!socio) return null;

    const cuotaMensual = simulacion?.[0]?.total || 0;
    const ingresoMensual = perfil360?.ingresoMensual || 0;
    const ratioCapacidad = ingresoMensual > 0 ? (cuotaMensual / ingresoMensual) * 100 : 0;
    const isExcedido = ratioCapacidad > 70;
    const score = 810; // High-fidelity spec

    return (
        <aside className="sticky top-0 h-screen w-72 bg-white border-r border-slate-200 z-30 flex flex-col overflow-hidden animate-in slide-in-from-left duration-500 shrink-0">
            {/* Header: Profile */}
            <div className="p-8 pb-6 border-b border-slate-100 bg-slate-50/50">
                <div className="relative mb-6">
                    <div className="w-20 h-20 rounded-[2rem] bg-slate-200 border-4 border-white shadow-xl overflow-hidden">
                        <img src={`https://ui-avatars.com/api/?name=${socio.nombres}+${socio.apellidos}&background=0D8ABC&color=fff&size=128`} alt="Socio" className="w-full h-full object-cover" />
                    </div>
                    <div className="absolute -bottom-2 -right-2 bg-amber-400 text-white text-[8px] font-black px-2 py-1 rounded-full shadow-lg uppercase tracking-widest border-2 border-white">
                        ORO
                    </div>
                </div>
                <h3 className="text-lg font-black text-slate-800 leading-tight uppercase tracking-tight">{socio.nombres} {socio.apellidos}</h3>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">C.I.: {new Intl.NumberFormat('es-PY').format(socio.numeroDocumento || 0)}</p>
                <div className="mt-4 inline-flex items-center gap-2 px-3 py-1 bg-emerald-50 text-emerald-600 rounded-full border border-emerald-100">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                    <span className="text-[9px] font-black uppercase tracking-widest">Socio Activo</span>
                </div>
            </div>

            {/* Content: Financial Context & Breakdown */}
            <div className="flex-1 overflow-y-auto p-8 space-y-10">
                {/* Score & Capacity */}
                <div>
                    <div className="flex justify-between items-end mb-3">
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Capacidad Pago</span>
                        <span className={`text-xs font-black ${isExcedido ? 'text-rose-600' : 'text-emerald-600'}`}>{ratioCapacidad.toFixed(1)}% Usado</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden flex">
                        <div className={`h-full transition-all duration-500 ${isExcedido ? 'bg-rose-500' : 'bg-gradient-to-r from-blue-500 to-emerald-500'}`} style={{ width: `${Math.min(100, ratioCapacidad)}%` }}>
                            <div className="h-full bg-white/20 animate-pulse" style={{ width: '100%' }}></div>
                        </div>
                    </div>
                    {isExcedido && <p className="text-[9px] font-bold text-rose-500 mt-2 uppercase tracking-tight">Criterio excedido (&gt;30%)</p>}
                </div>

                {/* Account Details */}
                <div className="space-y-6">
                    <div>
                        <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-2">Posición Consolidada</span>
                        <div className="space-y-3">
                            <div className="flex justify-between items-center p-3 bg-slate-50 rounded-xl border border-slate-100">
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center"><Wallet size={14} className="text-blue-600" /></div>
                                    <span className="text-[10px] font-bold text-slate-600 uppercase">Aportes APO</span>
                                </div>
                                <span className="text-xs font-black text-slate-800 font-mono">{new Intl.NumberFormat('es-PY').format(perfil360?.saldoAportes || 0)}</span>
                            </div>
                        </div>
                    </div>

                    {/* LIQUIDATION PREVIEW (NEW) */}
                    {perfil360?.listaCreditos && perfil360.listaCreditos.length > 0 && (
                        <div className="pt-6 border-t border-slate-100 mb-6">
                            <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-4">Créditos Vigentes / Historial</span>
                            <div className="space-y-3">
                                {perfil360.listaCreditos.map((cred, idx) => (
                                    <div key={idx} className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                                        <div className="flex justify-between items-start mb-2">
                                            <span className="text-[10px] font-black text-slate-800 uppercase">#{cred.id} {cred.nroOperacion ? `- ${cred.nroOperacion}` : ''}</span>
                                            <span className={`text-[8px] font-black px-2 py-0.5 rounded-full uppercase ${cred.estadoValor === 'DES' ? 'bg-emerald-100 text-emerald-600' :
                                                cred.estadoValor === 'CAN' ? 'bg-slate-200 text-slate-600' :
                                                    'bg-amber-100 text-amber-600'
                                                }`}>
                                                {cred.estado}
                                            </span>
                                        </div>
                                        <div className="flex justify-between items-center text-[10px] font-bold text-slate-500">
                                            <span>Cuotas: {cred.cuotasPagadas}/{cred.totalCuotas}</span>
                                            <span className="text-slate-900 font-mono">{new Intl.NumberFormat('es-PY').format(cred.saldoCapitalPendiente || 0)}</span>
                                        </div>
                                        <div className="mt-2 h-1 bg-slate-200 rounded-full overflow-hidden">
                                            <div
                                                className="h-full bg-emerald-500"
                                                style={{ width: `${(cred.cuotasPagadas / cred.totalCuotas) * 100}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* LIQUIDATION PREVIEW (NEW) */}
                    {simulacion && simulacion.length > 0 && (
                        <div className="pt-6 border-t border-slate-100 animate-in fade-in">
                            <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-4">Proyección de Desembolso</span>
                            <div className="space-y-3">
                                <div className="flex justify-between text-[10px] font-bold text-slate-500">
                                    <span>Capital Bruto</span>
                                    <span>{new Intl.NumberFormat('es-PY').format(simulacion.reduce((acc, c) => acc + c.capital, 0) || 0)}</span>
                                </div>
                                <div className="flex justify-between text-[10px] font-bold text-slate-500">
                                    <span>Intereses a Devengar</span>
                                    <span>{new Intl.NumberFormat('es-PY').format(simulacion.reduce((acc, c) => acc + c.interes, 0) || 0)}</span>
                                </div>
                                <div className="flex justify-between text-[10px] font-bold text-slate-500">
                                    <span>IVA Estimado (10%)</span>
                                    <span>{new Intl.NumberFormat('es-PY').format(simulacion.reduce((acc, c) => acc + c.iva, 0) || 0)}</span>
                                </div>
                                <div className="flex justify-between text-[10px] font-bold text-rose-500">
                                    <span>Gastos Administrativos</span>
                                    <span>- 20.000</span>
                                </div>
                                <div className="flex justify-between text-[10px] font-bold text-rose-500">
                                    <span>Aporte Extra (2%)</span>
                                    <span>- {new Intl.NumberFormat('es-PY').format((simulacion.reduce((acc, c) => acc + c.capital, 0) || 0) * 0.02)}</span>
                                </div>

                                <div className="mt-4 pt-3 border-t border-slate-100 flex justify-between items-center">
                                    <span className="text-[10px] font-black uppercase text-emerald-600">Neto a Desembolsar</span>
                                    <span className="text-sm font-black text-emerald-600 font-mono">
                                        Gs {new Intl.NumberFormat('es-PY').format((simulacion.reduce((acc, c) => acc + c.capital, 0) || 0) - 20000 - ((simulacion.reduce((acc, c) => acc + c.capital, 0) || 0) * 0.02))}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Footer: Advisor Context */}
            <div className="p-6 bg-slate-900 text-white">
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-400 font-black text-[10px]">A1</div>
                    <div>
                        <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Asesor en Sesión</p>
                        <p className="text-[10px] font-black text-white uppercase">Oficina Central</p>
                    </div>
                </div>
            </div>
        </aside>
    );
};

// --- MODERN UI BENTO HELPERS ---

const BentoCard = ({ title, children, icon: Icon, className = "" }) => (
    <div className={`bg-white rounded-3xl p-8 shadow-xl shadow-slate-200/50 border border-slate-100 relative overflow-hidden group hover:shadow-2xl transition-all duration-500 ${className}`}>
        {Icon && (
            <div className="absolute top-0 right-0 p-8 opacity-[0.03] group-hover:opacity-[0.08] transition-opacity rotate-12">
                <Icon size={120} />
            </div>
        )}
        <div className="relative z-10 h-full flex flex-col">
            {title && (
                <div className="flex items-center gap-3 mb-8">
                    {Icon && <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center text-slate-400 group-hover:bg-blue-50 group-hover:text-blue-500 transition-colors"><Icon size={18} /></div>}
                    <h4 className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-800">{title}</h4>
                </div>
            )}
            <div className="flex-1">{children}</div>
        </div>
    </div>
);

const ModernSlider = ({ label, value, min, max, step = 100000, onChange, suffix = "GS" }) => (
    <div className="space-y-6">
        <div className="flex justify-between items-end">
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{label}</span>
            <span className="text-lg font-black text-slate-900 font-mono">
                {new Intl.NumberFormat('es-PY').format(value)} <small className="text-[10px] text-slate-400 font-sans">{suffix}</small>
            </span>
        </div>
        <div className="relative pt-2">
            <input
                type="range"
                min={min}
                max={max}
                step={step}
                value={value}
                onChange={e => onChange(e.target.value)}
                className="w-full h-1.5 bg-slate-100 rounded-full appearance-none cursor-pointer accent-blue-600 focus:outline-none"
            />
            <div className="flex justify-between mt-2">
                <span className="text-[9px] font-bold text-slate-300 uppercase">{new Intl.NumberFormat('es-PY').format(min)}</span>
                <span className="text-[9px] font-bold text-slate-300 uppercase">{new Intl.NumberFormat('es-PY').format(max)}</span>
            </div>
        </div>
    </div>
);

const CapacityChart = ({ capacity, projected }) => {
    const ratio = Math.min(100, (projected / capacity) * 100);
    const availableRatio = 100 - ratio;

    return (
        <div className="space-y-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Margen Disponible</p>
                    <div className="flex items-center gap-2">
                        <span className={`text-2xl font-black ${availableRatio < 30 ? 'text-rose-600' : 'text-emerald-500'}`}>{availableRatio.toFixed(1)}%</span>
                        {availableRatio >= 30 && <CheckCircle className="text-emerald-500" size={20} />}
                    </div>
                </div>
                <div className="sm:text-right">
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Cuota vs Ingreso</p>
                    <p className={`text-[10px] font-black uppercase leading-tight ${availableRatio < 30 ? 'text-rose-600' : 'text-slate-500'}`}>
                        {availableRatio < 30 ? 'CRITERIO EXCEDIDO' : 'DENTRO DE RANGO'}
                    </p>
                </div>
            </div>

            <div className="space-y-4">
                <div className="space-y-2">
                    <div className="flex justify-between text-[10px] font-bold uppercase tracking-tight">
                        <span className="text-slate-400">Capacidad Teórica</span>
                        <span className="text-slate-800">{new Intl.NumberFormat('es-PY').format(capacity)} GS</span>
                    </div>
                    <div className="h-4 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-slate-300" style={{ width: '100%' }}></div>
                    </div>
                </div>
                <div className="space-y-2">
                    <div className="flex justify-between text-[10px] font-bold uppercase tracking-tight">
                        <span className="text-slate-400">Cuota Proyectada</span>
                        <span className="text-rose-500">{new Intl.NumberFormat('es-PY').format(projected)} GS</span>
                    </div>
                    <div className="h-4 bg-slate-100 rounded-full overflow-hidden">
                        <div className={`h-full ${availableRatio < 30 ? 'bg-rose-500' : 'bg-blue-600'}`} style={{ width: `${ratio}%` }}></div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const CustomSelect = ({ label, value, onChange, options, disabled, placeholder, icon: Icon }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const selectedOption = options.find(opt => String(opt.value) === String(value));

    return (
        <div className="relative" ref={dropdownRef}>
            {label && <label className="text-[10px] font-black text-slate-500 uppercase block mb-1 tracking-wider">{label}</label>}
            <div
                onClick={() => !disabled && setIsOpen(!isOpen)}
                className={`border bg-white p-2 w-full rounded focus-within:ring-1 focus-within:ring-blue-500 transition-all flex justify-between items-center cursor-pointer select-none
                    ${disabled ? 'bg-slate-50 border-slate-100 cursor-not-allowed text-slate-400 font-mono text-[11px]' : 'border-slate-300 hover:border-slate-400 text-slate-800 font-mono text-[11px] font-black'}`}
            >
                <div className="flex items-center gap-2 overflow-hidden">
                    {Icon && <Icon size={12} className={disabled ? 'text-slate-300' : 'text-slate-400'} />}
                    <span className="truncate">{selectedOption ? selectedOption.label : placeholder || '...'}</span>
                </div>
                <ChevronDown className={`transition-transform duration-200 text-slate-400 flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} size={12} />
            </div>

            {isOpen && (
                <div className="absolute z-[100] w-full mt-1 bg-white border border-slate-300 rounded shadow-xl overflow-hidden max-h-[350px] overflow-y-auto">
                    {options.length > 0 ? options.map((opt, i) => (
                        <div
                            key={i}
                            onClick={() => {
                                onChange(opt.value);
                                setIsOpen(false);
                            }}
                            className={`p-2.5 cursor-pointer hover:bg-slate-50 transition-colors border-b border-slate-50 last:border-0 flex items-center justify-between text-xs font-mono
                                ${String(opt.value) === String(value) ? 'bg-blue-50 text-blue-700 font-black' : 'text-slate-700'}`}
                        >
                            <span>{opt.label}</span>
                            {String(opt.value) === String(value) && <CheckCircle size={12} className="text-blue-500" />}
                        </div>
                    )) : (
                        <div className="p-4 text-slate-400 italic text-center text-xs">Sin opciones</div>
                    )}
                </div>
            )}
        </div>
    );
};

const numberToWords = (rawNum) => {
    if (rawNum === null || rawNum === undefined || isNaN(rawNum)) return 'CERO';
    const num = Math.floor(Math.abs(rawNum));
    const unidades = ['', 'UN', 'DOS', 'TRES', 'CUATRO', 'CINCO', 'SEIS', 'SIETE', 'OCHO', 'NUEVE'];
    const decenas = ['', 'DIEZ', 'VEINTE', 'TREINTA', 'CUARENTA', 'CINCUENTA', 'SESENTA', 'SETENTA', 'OCHENTA', 'NOVENTA'];
    const especiales = { 11: 'ONCE', 12: 'DOCE', 13: 'TRECE', 14: 'CATORCE', 15: 'QUINCE', 10: 'DIEZ', 20: 'VEINTE' };
    const centenas = ['', 'CIENTO', 'DOSCIENTOS', 'TRESCIENTOS', 'CUATROCIENTOS', 'QUINIENTOS', 'SEISCIENTOS', 'SETECIENTOS', 'OCHOCIENTOS', 'NOVECIENTOS'];

    if (num === 0) return 'CERO';
    if (num === 100) return 'CIEN';

    const convertirSeccion = (n) => {
        let output = '';
        if (n >= 100) {
            output += centenas[Math.floor(n / 100)] + ' ';
            n %= 100;
        }
        if (n >= 10 && n <= 20 && especiales[n]) {
            output += especiales[n] + ' ';
            n = 0;
        } else if (n >= 10) {
            output += decenas[Math.floor(n / 10)] + (n % 10 !== 0 ? ' Y ' : ' ');
            n %= 10;
        }
        if (n > 0) {
            output += unidades[n] + ' ';
        }
        return output;
    };

    let result = '';
    const millones = Math.floor(num / 1000000);
    const miles = Math.floor((num % 1000000) / 1000);
    const resto = num % 1000;

    if (millones > 0) {
        result += millones === 1 ? 'UN MILLÓN ' : convertirSeccion(millones) + 'MILLONES ';
    }
    if (miles > 0) {
        result += miles === 1 ? 'MIL ' : convertirSeccion(miles) + 'MIL ';
    }
    if (resto > 0) {
        result += convertirSeccion(resto);
    }

    return result.trim();
};


const SolicitudCredito = () => {
    const { token } = useAuth();
    const navigate = useNavigate();

    // UI State
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [showCamera, setShowCamera] = useState(false);
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const [activeDocType, setActiveDocType] = useState(null); // Which doc is being captured

    // Data State
    const [socio, setSocio] = useState(null);
    const [perfil360, setPerfil360] = useState(null);
    const [loadingPerfil, setLoadingPerfil] = useState(false);
    const [error, setError] = useState(null);
    const [departamentos, setDepartamentos] = useState([]);
    const [destinos, setDestinos] = useState([]);
    const [lineas, setLineas] = useState([]);
    const [tiposIngreso, setTiposIngreso] = useState([]);
    const [profesiones, setProfesiones] = useState([]);
    const [rangosAntiguedad, setRangosAntiguedad] = useState([]);
    const [regla, setRegla] = useState(null);
    const [simulacion, setSimulacion] = useState(null);
    const [viabilidadData, setViabilidadData] = useState(null);
    const [showSimulateModal, setShowSimulateModal] = useState(false);
    const [modalPage, setModalPage] = useState(1);

    // Step 1: Entrevista State
    const [contactoForm, setContactoForm] = useState({
        celular1: '',
        celular2: '',
        email1: '',
        email2: ''
    });
    const [address, setAddress] = useState(null); // Existing address
    const [referencias, setReferencias] = useState([]);
    const [verificado, setVerificado] = useState(false);
    const [ingresos, setIngresos] = useState([]);
    const [servicios, setServicios] = useState([]);
    const [laboralForm, setLaboralForm] = useState({
        empresa: '',
        profesionId: '',
        rangoAntiguedadId: '',
        tipoOcupacion: 'DEPENDIENTE',
        direccion: '',
        telefono: ''
    });

    // Address State (for new address creation)
    const [addressForm, setAddressForm] = useState({
        departamentoId: '',
        ciudadId: '',
        barrioId: '',
        nroCasa: '',
        direccionExacta: '',
        referencia: '',
        tipoDireccion: 'PARTICULAR'
    });
    const [fileIngreso, setFileIngreso] = useState(null);
    const [ciudades, setCiudades] = useState([]);
    const [barrios, setBarrios] = useState([]);

    // Form State
    const [form, setForm] = useState({
        lineaCreditoId: '',
        monto: '',
        plazo: '',
        destinoId: '',
        observacion: '',
        metodoAmortizacionId: 1,
        fechaPrimerVencimiento: ''
    });

    const steps = [
        { id: 1, title: 'Socio & Riesgo', icon: ShieldCheck },
        { id: 2, title: 'Estructura & Garantía', icon: Calculator },
        { id: 3, title: 'Referencias & Contacto', icon: User },
        { id: 4, title: 'Cierre Documental', icon: FileText }
    ];

    const [refInput, setRefInput] = useState({ nombre: '', telefono: '', tipo: 'PERSONAL' });

    const [documentos, setDocumentos] = useState([]); // { tipo: 'INGRESOS', nombre: 'file.jpg', file: (File or Base64) }

    // Step 3: Legajo Físico State
    const [legajo, setLegajo] = useState({
        tieneCedula: false,
        tieneLiquidacion: false,
        montoIngresoReal: '',
        tieneCertificadoTrabajo: false,
        tieneFacturaServicio: false,
        ubicacionFisica: ''
    });

    const [savedSolicitudId, setSavedSolicitudId] = useState(null);
    const [antiguedadSocio, setAntiguedadSocio] = useState(0);

    // Step 4: Legal Printing State
    const [entidad, setEntidad] = useState(null);
    const [plantillaPagare, setPlantillaPagare] = useState(null);
    const [plantillaInformconf, setPlantillaInformconf] = useState(null);
    const [plantillaContrato, setPlantillaContrato] = useState(null);
    const [plantillaLiquidacion, setPlantillaLiquidacion] = useState(null);
    const [corroborado, setCorroborado] = useState(false);

    // --- TEMPLATE ENGINE (HIGH-FIDELITY LEGAL KIT v2) ---

    const fmt = (n) => {
        if (n === null || n === undefined || isNaN(n)) return '0';
        return new Intl.NumberFormat('es-PY').format(n);
    };

    // Seguro de Vida: 0.8% del capital solicitado, prorrateado entre cuotas
    const calcSeguroVida = () => {
        const capital = parseFloat(form.monto) || 0;
        const plazo = parseInt(form.plazo) || 1;
        const totalSeguro = Math.round(capital * 0.008);
        const seguroPorCuota = Math.round(totalSeguro / plazo);
        return { totalSeguro, seguroPorCuota };
    };

    const generateDocumentHeader = (docName) => {
        const logoUrl = entidad?.urlLogo || '';
        const entNombre = entidad?.nombreCooperativa || 'COOPERATIVA DE TRABAJO';
        const entRuc = entidad?.ruc || '80000000-0';
        const entDir = entidad?.direccionCasaMatriz || 'ASUNCI&Oacute;N, PARAGUAY';
        const socioId = socio?.numeroSocio || socio?.id || '000000';
        const fecha = new Date().toLocaleDateString('es-PY');
        return `
        <table style="width:100%; border-collapse:collapse; margin-bottom:15px; font-family:Arial, sans-serif;">
            <tr>
                <td style="width:35%; vertical-align:top; padding:5px;">
                    ${logoUrl ? `<img src="${logoUrl}" style="height:55px; margin-bottom:5px;" alt="Logo"/>` : ''}
                    <div style="font-size:11px; font-weight:900; color:#1a202c; text-transform:uppercase;">${entNombre}</div>
                    <div style="font-size:9px; color:#4a5568; line-height:1.2; margin-top:2px;">RUC: ${entRuc}<br/>${entDir}</div>
                </td>
                <td style="width:30%; text-align:center; vertical-align:middle; padding:10px;">
                    <div style="font-size:16px; font-weight:900; letter-spacing:1px; color:#2d3748; text-transform:uppercase; border-bottom:3px solid #1a202c; padding-bottom:5px; margin-bottom:3px;">${docName}</div>
                    <div style="font-size:9px; color:#718096; font-weight:bold; letter-spacing:1px;">KIT LEGAL DE CR&Eacute;DITO v2.0</div>
                </td>
                <td style="width:35%; vertical-align:top; padding:5px;">
                    <div style="border:1px solid #cbd5e0; border-radius:4px; padding:10px; font-size:10px; line-height:1.6; background:#f7fafc; color:#2d3748;">
                        <table style="width:100%; border-collapse:collapse;">
                            <tr><td style="font-weight:bold; padding-bottom:2px;">Socio N&ordm;:</td><td style="text-align:right; font-weight:900;">${socioId}</td></tr>
                            <tr><td style="font-weight:bold; padding-bottom:2px;">Op. N&ordm;:</td><td style="text-align:right; font-weight:900; color:#c53030;">#${savedSolicitudId || 'PENDIENTE'}</td></tr>
                            <tr><td style="font-weight:bold;">Fecha:</td><td style="text-align:right; font-weight:bold;">${fecha}</td></tr>
                        </table>
                    </div>
                </td>
            </tr>
        </table>
        `;
    };

    const generateSocioBlock = () => {
        const nombre = `${socio?.nombres || socio?.personaFisica?.nombres || ''} ${socio?.apellidos || socio?.personaFisica?.apellidos || ''}`.trim();
        const ci = fmt(socio?.numeroDocumento || 0);
        const dir = `${address?.direccionExacta || ''}, ${address?.nroCasa || 'S/N'} (${address?.nombreBarrio || ''})`;
        const tel = socio?.contactos?.find(c => c.tipoContacto === 'CELULAR' || c.tipo === 'CELULAR')?.valor || 'S/D';
        return `
        <table style="width:100%; font-size:10px; margin-bottom:12px; border-collapse:collapse;">
            <tr>
                <td style="padding:3px 0;"><b>Nombre Completo:</b> ${nombre}</td>
                <td style="padding:3px 0;"><b>C.I. N&ordm;:</b> ${ci}</td>
            </tr>
            <tr>
                <td style="padding:3px 0;"><b>Domicilio:</b> ${dir}</td>
                <td style="padding:3px 0;"><b>Tel&eacute;fono:</b> ${tel}</td>
            </tr>
        </table>
        `;
    };

    const formatLiquidationTable = () => {
        if (!simulacion || simulacion.length === 0) return "";

        const requestedMonto = parseFloat(form.monto) || 0;
        const { seguroPorCuota, totalSeguro } = calcSeguroVida();
        let runningAmortTotal = 0;

        let rows = simulacion.map((c, index) => {
            const isLast = index === simulacion.length - 1;
            const interes = c.interes || 0;
            const iva = c.iva || 0;
            const seguro = seguroPorCuota;

            let amortizacion = (c.total || 0) - interes - iva;

            if (isLast) {
                amortizacion = requestedMonto - runningAmortTotal;
            }
            runningAmortTotal += amortizacion;

            const cuotaTotal = amortizacion + interes + iva + seguro;
            const saldoCapital = isLast ? 0 : (c.saldo || 0);

            return `
                <tr style="font-size:10px; border-bottom:1px solid #edf2f7;">
                    <td style="padding:6px; text-align:center; font-family:monospace; background:#f8fafc; border:1px solid #cbd5e0;">${c.numeroCuota || c.nroCuota}</td>
                    <td style="padding:6px; text-align:center; border:1px solid #cbd5e0;">${c.vencimiento || c.fechaVencimiento}</td>
                    <td style="padding:6px; text-align:right; border:1px solid #cbd5e0;">${fmt(amortizacion)}</td>
                    <td style="padding:6px; text-align:right; border:1px solid #cbd5e0;">${fmt(interes)}</td>
                    <td style="padding:6px; text-align:right; border:1px solid #cbd5e0;">${fmt(iva)}</td>
                    <td style="padding:6px; text-align:right; border:1px solid #cbd5e0;">${fmt(seguro)}</td>
                    <td style="padding:6px; text-align:right; font-weight:bold; background:#f7fafc; border:1px solid #cbd5e0;">${fmt(cuotaTotal)}</td>
                    <td style="padding:6px; text-align:right; color:#718096; font-size:9px; border:1px solid #cbd5e0;">${fmt(saldoCapital)}</td>
                </tr>
            `;
        }).join("");

        const totalInt = simulacion.reduce((acc, c) => acc + (c.interes || 0), 0);
        const totalIva = simulacion.reduce((acc, c) => acc + (c.iva || 0), 0);
        const totalAmort = runningAmortTotal;
        const totalCuota = totalAmort + totalInt + totalIva + totalSeguro;

        return `
            <table style="width:100%; border-collapse:collapse; font-family:Arial,sans-serif; margin-top:10px;">
                <thead>
                    <tr style="background:#2d3748; color:white; font-size:9px; font-weight:bold; text-transform:uppercase; letter-spacing:0.5px;">
                        <th style="padding:8px; border:1px solid #2d3748;">N&ordm; Cuota</th>
                        <th style="padding:8px; border:1px solid #2d3748;">Vencimiento</th>
                        <th style="padding:8px; border:1px solid #2d3748;">Amortizaci&oacute;n</th>
                        <th style="padding:8px; border:1px solid #2d3748;">Inter&eacute;s</th>
                        <th style="padding:8px; border:1px solid #2d3748;">IVA (10%)</th>
                        <th style="padding:8px; border:1px solid #2d3748;">Seguro PP</th>
                        <th style="padding:8px; border:1px solid #2d3748;">Importe Total</th>
                        <th style="padding:8px; border:1px solid #2d3748;">Saldo Capital</th>
                    </tr>
                </thead>
                <tbody>
                    ${rows}
                    <tr style="font-weight:900; background:#edf2f7; font-size:10px;">
                        <td colspan="2" style="padding:8px; text-align:center; border:1px solid #cbd5e0;">TOTALES CONSOLIDADOS (Gs.)</td>
                        <td style="padding:8px; text-align:right; border:1px solid #cbd5e0;">${fmt(totalAmort)}</td>
                        <td style="padding:8px; text-align:right; border:1px solid #cbd5e0;">${fmt(totalInt)}</td>
                        <td style="padding:8px; text-align:right; border:1px solid #cbd5e0;">${fmt(totalIva)}</td>
                        <td style="padding:8px; text-align:right; border:1px solid #cbd5e0;">${fmt(totalSeguro)}</td>
                        <td style="padding:8px; text-align:right; border:1px solid #cbd5e0; font-size:11px; color:#2c5282;">${fmt(totalCuota)}</td>
                        <td style="padding:8px; border:1px solid #cbd5e0;"></td>
                    </tr>
                </tbody>
            </table>
        `;
    };

    const generateDesembolsoSummary = () => {
        const capital = parseFloat(form.monto) || 0;
        const { totalSeguro } = calcSeguroVida();
        const totalInt = (simulacion || []).reduce((acc, c) => acc + (c.interes || 0), 0);
        const totalIva = (simulacion || []).reduce((acc, c) => acc + (c.iva || 0), 0);
        const totalDeuda = capital + totalInt + totalIva + totalSeguro;

        // Business Logic: Administrative Expenses (Flat) and Contributions (Percentage)
        const gastosAdmin = 20000;
        const aportes = Math.round(capital * 0.02); // 2% of capital
        const neto = capital - gastosAdmin - aportes;

        return `
        <div style="margin-top:20px; border:2px solid #edf2f7; border-radius:8px; overflow:hidden; font-size:10px; font-family:Arial,sans-serif; background:#fff;">
            <div style="background:#2d3748; color:white; font-weight:900; font-size:12px; padding:10px; text-transform:uppercase; letter-spacing:1px; text-align:center;">
                An&aacute;lisis de Liquidaci&oacute;n y Desembolso
            </div>
            <div style="padding:15px;">
                <table style="width:100%; font-size:11px; border-collapse:collapse;">
                    <tr><td style="padding:5px 0; color:#4a5568;">Capital Bruto Solicitado:</td><td style="text-align:right; font-weight:bold; font-size:12px;">Gs. ${fmt(capital)}</td></tr>
                    <tr><td style="padding:5px 0; color:#4a5568;">Intereses Totales a Devengar:</td><td style="text-align:right;">Gs. ${fmt(totalInt)}</td></tr>
                    <tr><td style="padding:5px 0; color:#4a5568;">IVA Crédito (10%):</td><td style="text-align:right;">Gs. ${fmt(totalIva)}</td></tr>
                    <tr><td style="padding:5px 0; color:#4a5568;">Seguro de Protecci&oacute;n de Pr&eacute;stamo:</td><td style="text-align:right;">Gs. ${fmt(totalSeguro)}</td></tr>
                    <tr style="border-top:1px solid #cbd5e0; border-bottom:1px double #cbd5e0;">
                        <td style="padding:8px 0; font-weight:900; color:#2d3748;">TOTAL COMPROMISO DE PAGO:</td>
                        <td style="text-align:right; font-weight:900; font-size:14px; color:#2c5282;">Gs. ${fmt(totalDeuda)}</td>
                    </tr>
                </table>
                
                <div style="margin-top:15px; background:#fefcbf; border:1px solid #faf089; border-radius:6px; padding:12px;">
                    <div style="font-weight:bold; color:#744210; margin-bottom:5px; text-transform:uppercase; font-size:9px;">Deducciones y Gastos Operativos</div>
                    <table style="width:100%; font-size:11px;">
                        <tr><td style="padding:3px 0;">Gastos Administrativos / Formularios:</td><td style="text-align:right; color:#c53030; font-weight:bold;">- Gs. ${fmt(gastosAdmin)}</td></tr>
                        <tr><td style="padding:3px 0;">Aporte Especial por Crédito (2%):</td><td style="text-align:right; color:#c53030; font-weight:bold;">- Gs. ${fmt(aportes)}</td></tr>
                    </table>
                </div>

                <div style="margin-top:15px; background:#f0fff4; border:2px solid #68d391; border-radius:8px; padding:15px; display:flex; justify-content:space-between; align-items:center;">
                    <div style="font-weight:900; font-size:14px; color:#22543d; text-transform:uppercase;">Neto Gs. a Entregar:</div>
                    <div style="text-align:right; font-weight:900; font-size:22px; color:#276749;">Gs. ${fmt(neto)}</div>
                </div>
            </div>
        </div>

        <div style="margin-top:50px; font-size:10px; color:#4a5568; line-height:1.6; text-align:justify; border-top:1px solid #eee; pt:10px;">
            <p>Declaramos bajo fe de juramento que los datos arriba consignados son fiel reflejo de la operaci&oacute;n pactada. El socio autoriza a la Cooperativa a realizar las deducciones pertinentes seg&uacute;n el reglamento de cr&eacute;dito vigente.</p>
        </div>

        <div style="margin-top:100px; display:flex; justify-content:space-between; font-size:10px; font-family:Arial,sans-serif;">
            <div style="text-align:center; width:45%;">
                <div style="border-top:1px solid #000; margin-bottom:5px;"></div>
                <b style="text-transform:uppercase;">${socio?.nombres || socio?.personaFisica?.nombres || ''} ${socio?.apellidos || socio?.personaFisica?.apellidos || ''}</b><br/>
                Socio Solicitante - C.I. ${fmt(socio?.numeroDocumento || 0)}
            </div>
            <div style="text-align:center; width:45%;">
                <div style="border-top:1px solid #000; margin-bottom:5px;"></div>
                <b style="text-transform:uppercase;">Oficial de Cr&eacute;dito</b><br/>
                Firma y Sello Autorizado
            </div>
        </div>
        `;
    };

    const renderFullLiquidation = () => {
        if (!simulacion || simulacion.length === 0) return "<p>Debe generar la simulaci&oacute;n primero.</p>";
        return `
            <div style="font-family:Arial,Helvetica,sans-serif; color:#1a1a1a; line-height:1.4;">
                ${generateDocumentHeader('LIQUIDACI&Oacute;N DE DESEMBOLSO')}
                ${generateSocioBlock()}
                <div style="font-size:10px; margin-bottom:6px;">
                    <b>L&iacute;nea de Cr&eacute;dito:</b> ${lineas.find(l => String(l.id) === String(form.lineaCreditoId))?.nombre || 'N/A'} &nbsp;|&nbsp;
                    <b>Plazo:</b> ${form.plazo || 0} meses &nbsp;|&nbsp;
                    <b>TNA:</b> ${regla?.tasaInteres || 0}%
                </div>
                ${formatLiquidationTable()}
                ${generateDesembolsoSummary()}
            </div>
        `;
    };

    const generateDocumentFooter = (leftLabel = "Firma del Socio", rightLabel = "Firma Autorizada") => {
        const nombre = `${socio?.nombres || socio?.personaFisica?.nombres || ''} ${socio?.apellidos || socio?.personaFisica?.apellidos || ''}`.trim();
        const ci = fmt(socio?.numeroDocumento || 0);

        return `
        <div style="margin-top:80px; display:flex; justify-content:space-between; font-size:10px; font-family:Arial,sans-serif;">
            <div style="text-align:center; width:43%;">
                <div style="border-top:1.5px solid #2d3748; margin-bottom:5px;"></div>
                <b style="text-transform:uppercase;">${nombre}</b><br/>
                ${leftLabel} - C.I. ${ci}
            </div>
            <div style="text-align:center; width:43%;">
                <div style="border-top:1.5px solid #2d3748; margin-bottom:5px;"></div>
                <b style="text-transform:uppercase;">${entidad?.nombreCooperativa || 'LA COOPERATIVA'}</b><br/>
                ${rightLabel}
            </div>
        </div>
        `;
    };

    const renderFullPagare = () => {
        const totalInt = (simulacion || []).reduce((acc, c) => acc + (c.interes || 0), 0);
        const totalIva = (simulacion || []).reduce((acc, c) => acc + (c.iva || 0), 0);
        const { totalSeguro } = calcSeguroVida();
        const capital = parseFloat(form.monto) || 0;
        const totalDeuda = capital + totalInt + totalIva + totalSeguro;
        const entNombre = entidad?.nombreCooperativa || 'COOPERATIVA DE TRABAJO';
        const entCiudad = 'ASUNCI&Oacute;N';
        const tasa = regla?.tasaInteres || 0;

        return `
        <div style="font-family:Arial, Helvetica, sans-serif; color:#1a1a1a; line-height:1.6; padding:0 10px;">
            ${generateDocumentHeader('PAGAR&Eacute; A LA ORDEN')}
            
            <div style="display:flex; justify-content:flex-end; margin:25px 0;">
                <div style="border:2px solid #1a202c; padding:10px 20px; font-size:18px; font-weight:900; background:#f7fafc; border-radius:4px;">
                    VALOR Gs.: ${fmt(totalDeuda)}
                </div>
            </div>

            <p style="text-align:justify; font-size:12px; margin:20px 0; line-height:1.8;">
                En la ciudad de <b>${entCiudad}</b>, Rep&uacute;blica del Paraguay, a los ${new Date().getDate()} d&iacute;as del mes de 
                <b>${new Date().toLocaleDateString('es-PY', { month: 'long' }).toUpperCase()}</b> del a&ntilde;o <b>${new Date().getFullYear()}</b>, 
                por este <b>PAGAR&Eacute; A LA ORDEN</b> me obligo a pagar incondicionalmente a la orden de 
                <b>${entNombre}</b>, o a quien sus derechos represente, en su domicilio social o donde se me reclame, 
                la cantidad de <b>GUARAN&Iacute;ES ${numberToWords(totalDeuda).toUpperCase()} (Gs. ${fmt(totalDeuda)})</b>, 
                por igual valor recibido en efectivo/cr&eacute;dito a mi entera satisfacci&oacute;n, seg&uacute;n Operaci&oacute;n 
                <b>N&ordm; #${savedSolicitudId || 'PENDIENTE'}</b>.
            </p>

            <p style="text-align:justify; font-size:11px; margin:15px 0;">
                Este pr&eacute;stamo devengar&aacute; una tasa de inter&eacute;s compensatorio del <b>${tasa}%</b> anual, calculado sobre saldos, 
                siendo el mismo pagadero en <b>${form.plazo || 0}</b> cuotas mensuales, iguales y consecutivas. 
                El incumplimiento de cualquiera de las cuotas pactadas producir&aacute; la mora de pleno derecho sin necesidad de interpelaci&oacute;n alguna 
                y facultar&aacute; a la ACREEDORA a dar por vencidos todos los plazos y exigir el pago total de la deuda.
            </p>

            <div style="margin:20px 0; padding:12px; border:1px solid #cbd5e0; background:#f8fafc; border-radius:6px; font-size:10px;">
                <b style="text-decoration:underline;">COMPOSICI&Oacute;N DEL VALOR ADMITIDO:</b><br/>
                <table style="width:100%; margin-top:5px; border-collapse:collapse;">
                    <tr>
                        <td>Capital: <b>Gs. ${fmt(capital)}</b></td>
                        <td>Intereses: <b>Gs. ${fmt(totalInt)}</b></td>
                        <td>IVA: <b>Gs. ${fmt(totalIva)}</b></td>
                        <td>Seguro PP: <b>Gs. ${fmt(totalSeguro)}</b></td>
                    </tr>
                </table>
            </div>

            ${generateDocumentFooter("Firma del Deudor Principal", "Oficial de Cr&eacute;ditos")}

            <div style="margin-top:40px; font-size:8px; color:#a0aec0; text-align:center;">
                Documento generado electr&oacute;nicamente - Cooperativa Core System v2.0
            </div>
        </div>
        `;
    };

    const renderFullContrato = () => {
        const nombre = `${socio?.nombres || socio?.personaFisica?.nombres || ''} ${socio?.apellidos || socio?.personaFisica?.apellidos || ''}`.trim();
        const ci = fmt(socio?.numeroDocumento || 0);
        const dir = `${address?.direccionExacta || ''}, ${address?.nroCasa || 'S/N'} (${address?.nombreBarrio || ''})`;
        const entNombre = entidad?.nombreCooperativa || 'COOPERATIVA DE TRABAJO';
        const capital = parseFloat(form.monto) || 0;
        const tasa = regla?.tasaInteres || 0;
        const plazo = form.plazo || 0;
        const totalInt = (simulacion || []).reduce((acc, c) => acc + (c.interes || 0), 0);
        const totalIva = (simulacion || []).reduce((acc, c) => acc + (c.iva || 0), 0);
        const { totalSeguro } = calcSeguroVida();
        const totalDeuda = capital + totalInt + totalIva + totalSeguro;
        const primerVto = form.fechaPrimerVencimiento ? new Date(form.fechaPrimerVencimiento).toLocaleDateString('es-PY') : '...';

        return `
        <div style="font-family:Arial, Helvetica, sans-serif; color:#1a1a1a; line-height:1.6; padding:0 20px;">
            ${generateDocumentHeader('CONTRATO DE PR&Eacute;STAMO')}
            
            <div style="margin:15px 0;">
                <div style="background:#edf2f7; padding:10px; border-radius:4px; font-size:11px; border-left:4px solid #2d3748;">
                    <b>IDENTIFICACI&Oacute;N DE LAS PARTES:</b><br/>
                    <b>ACREEDOR:</b> ${entNombre}<br/>
                    <b>DEUDOR:</b> ${nombre} | C.I. N&ordm; ${ci} | Domicilio: ${dir}
                </div>
            </div>

            <p style="text-align:justify; font-size:11px; margin:12px 0;">
                <b>CL&Aacute;USULA 1 - OBJETO Y MONTO:</b> La ACREEDORA entrega en este acto al DEUDOR/A la suma de <b>Gs. ${fmt(capital)} (${numberToWords(capital).toUpperCase()} GUARAN&Iacute;ES)</b>, 
                correspondiente a la Operaci&oacute;n <b>N&ordm; #${savedSolicitudId || 'PENDIENTE'}</b>. El DEUDOR declara recibir dicha suma a su entera satisfacci&oacute;n.
            </p>

            <p style="text-align:justify; font-size:11px; margin:12px 0;">
                <b>CL&Aacute;USULA 2 - CONDICIONES FINANCIERAS:</b> El DEUDOR se obliga a devolver el capital m&aacute;s un inter&eacute;s compensatorio del <b>${tasa}%</b> anual, 
                en un plazo de <b>${plazo}</b> meses, mediante cuotas mensuales consecutivas de <b>Gs. ${simulacion?.[0]?.total ? fmt(simulacion[0].total + calcSeguroVida().seguroPorCuota) : '...'}</b> aproximadamente (seg&uacute;n cuadro de liquidaci&oacute;n adjunto), venci&endo la primera cuota el <b>${primerVto}</b>.
            </p>

            <p style="text-align:justify; font-size:11px; margin:12px 0;">
                <b>CL&Aacute;USULA 3 - GARANT&Iacute;A Y MORA:</b> El DEUDOR constituye garant&iacute;a general sobre todos sus bienes y autoriza expresamente a la Cooperativa a afectar sus aportes y ahorros en caso de mora superior a 30 d&iacute;as. 
                La mora se producir&aacute; autom&aacute;ticamente por el solo vencimiento de los plazos.
            </p>

            <p style="text-align:justify; font-size:11px; margin:12px 0;">
                <b>CL&Aacute;USULA 4 - JURISDICCI&Oacute;N:</b> Para todos los efectos legales, las partes se someten a los Tribunales de la Ciudad de Asunci&oacute;n, prorrogando cualquier otra competencia.
            </p>

            ${generateDocumentFooter("Deudor / Socio", "Representante Legal")}
        </div>
        `;
    };

    const renderFullInformconf = () => {
        return `
        <div style="font-family:Arial,Helvetica,sans-serif; color:#1a1a1a; line-height:1.6; padding:0 20px;">
            ${generateDocumentHeader('AUTORIZACI&Oacute;N INFORMCONF')}
            
            <p style="text-align:justify; font-size:12px; margin:30px 0; line-height:1.8;">
                Por la presente autorizo en forma expresa, irrevocable y suficiente a la <b>${entidad?.nombreCooperativa || 'COOPERATIVA'}</b>, conforme a lo establecido en la Ley N&ordm; 6534/20 "DE PROTECCI&Oacute;N DE DATOS PERSONALES CREDITICIOS", a recabar informaci&oacute;n de las bases de datos de <b>EQUIFAX S.A. (INFORMCONF)</b> y/o cualquier otra entidad que procese datos comerciales y crediticios, sobre mi situaci&oacute;n patrimonial, solvencia econ&oacute;mica y cumplimiento de obligaciones.
            </p>

            <p style="text-align:justify; font-size:12px; margin:20px 0;">
                As&iacute; mismo, autorizo a reportar a dichas entidades los datos relativos a mi comportamiento crediticio, incluyendo atrasos o incumplimientos, para su inclusi&oacute;n en los reportes correspondientes.
            </p>

            ${generateDocumentFooter("Socio Solicitante", "Testigo / Oficial")}
        </div>
        `;
    };
    const handlePrint = () => {
        if (!simulacion || simulacion.length === 0) {
            toast.error("Debe generar la simulaci\u00f3n antes de imprimir.");
            return;
        }

        // Anti-NaN validation: scan rendered content for NaN/undefined
        const testLiq = renderFullLiquidation();
        const testPag = renderFullPagare();
        const nanInLiq = testLiq.includes('NaN') || testLiq.includes('undefined');
        const nanInPag = testPag.includes('NaN') || testPag.includes('undefined');
        if (nanInLiq || nanInPag) {
            const doc = nanInLiq ? 'Liquidación' : 'Pagaré';
            const content = nanInLiq ? testLiq : testPag;
            // Find the problematic snippet
            const nanIdx = content.indexOf('NaN') !== -1 ? content.indexOf('NaN') : content.indexOf('undefined');
            const snippet = content.substring(Math.max(0, nanIdx - 40), Math.min(content.length, nanIdx + 40));
            console.error(`NaN detectado en ${doc}:`, snippet);
            toast.error(`Error en ${doc}: valor inválido detectado. Ver consola F12 para detalles.`, {
                icon: '\u26a0\ufe0f', duration: 6000
            });
            return;
        }

        // Audit: Sum(Amortizations) == Capital
        const requestedMonto = parseFloat(form.monto) || 0;
        const totalInt = simulacion.reduce((acc, c) => acc + (c.interes || 0), 0);
        const totalIva = simulacion.reduce((acc, c) => acc + (c.iva || 0), 0);
        const totalCuota = simulacion.reduce((acc, c) => acc + (c.total || 0), 0);
        const totalAmort = totalCuota - totalInt - totalIva;

        if (Math.abs(totalAmort - requestedMonto) > 2) {
            toast.error("Auditor\u00eda fallida: El capital amortizado no coincide con el monto solicitado.");
            return;
        }

        window.print();
    };

    const renderLiquidation = () => renderFullLiquidation();

    // --- DERIVED STATE ---
    const isExcedido = useMemo(() => {
        if (!perfil360 || !simulacion || simulacion.length === 0) return false;
        const ingresoMensual = perfil360.ingresoMensual || 0;
        const cuota = simulacion[0].total || 0;
        if (ingresoMensual <= 0) return true;
        // Regla 70/30: La cuota no debe superar el 30% del ingreso mensual
        return cuota > (ingresoMensual * 0.3);
    }, [perfil360, simulacion]);

    // --- INITIAL DATA LOADING ---
    useEffect(() => {
        // Default first installment date: 30 days from now
        const d = new Date();
        d.setDate(d.getDate() + 30);
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        setForm(prev => ({ ...prev, fechaPrimerVencimiento: `${yyyy}-${mm}-${dd}` }));

        if (!token) return;
        const headers = { 'Authorization': `Bearer ${token}` };

        const fetchInitialData = async () => {
            try {
                const resps = await Promise.all([
                    fetch(`${API_BASE_URL}/ubicacion/departamentos`, { headers }),
                    fetch(`${API_BASE_URL}/catalogos/destinos`, { headers }),
                    fetch(`${API_BASE_URL}/lineas-credito`, { headers }),
                    fetch(`${API_BASE_URL}/catalogos/tipos-ingreso`, { headers }),
                    fetch(`${API_BASE_URL}/catalogos/profesiones`, { headers }),
                    fetch(`${API_BASE_URL}/catalogos/rangos-antiguedad`, { headers })
                ]);

                const [deps, dests, lins, tIngresos, profs, rangos, ent, pag, inf, con, liq] = await Promise.all([
                    ...resps.map(r => r.json()),
                    fetch(`${API_BASE_URL}/configuracion-entidad`, { headers }).then(r => r.json()),
                    fetch(`${API_BASE_URL}/plantillas-documento/codigo/PAG_CRED`, { headers }).then(r => r.json()),
                    fetch(`${API_BASE_URL}/plantillas-documento/codigo/AUT_INF`, { headers }).then(r => r.json()),
                    fetch(`${API_BASE_URL}/plantillas-documento/codigo/CON_CRED`, { headers }).then(r => r.json()),
                    fetch(`${API_BASE_URL}/plantillas-documento/codigo/LIQ_CRED`, { headers }).then(r => r.json())
                ]);
                setDepartamentos(deps);
                setDestinos(dests);
                setLineas(lins);
                setTiposIngreso(tIngresos);
                setProfesiones(profs);
                setRangosAntiguedad(rangos);
                setEntidad(ent);
                setPlantillaPagare(pag);
                setPlantillaInformconf(inf);
                setPlantillaContrato(con);
                setPlantillaLiquidacion(liq);
            } catch (error) {
                console.error("Error fetching initial data:", error);
                setError("Error al cargar datos iniciales.");
            }
        };
        fetchInitialData();
    }, [token]);

    // --- SOCIO & ADDRESS LOGIC ---
    useEffect(() => {
        if (socio && token) {
            // Fetch Address
            fetch(`${API_BASE_URL}/socios/${socio.id}/direccion`, { headers: { 'Authorization': `Bearer ${token}` } })
                .then(res => res.json())
                .then(data => {
                    setAddress(data); // If null, means no address
                })
                .catch(() => setAddress(null));

            // Fetch habilitadas lines based on seniority
            fetch(`${API_BASE_URL}/solicitudes/lineas-habilitadas?antiguedad=${antiguedadSocio}`, { headers: { 'Authorization': `Bearer ${token}` } })
                .then(res => {
                    if (!res.ok) throw new Error("Error en la respuesta del servidor");
                    return res.json();
                })
                .then(data => {
                    if (Array.isArray(data)) {
                        setLineas(data);
                        if (data.length === 0) {
                            toast(`El socio cuenta con ${antiguedadSocio} meses de antigüedad. No califica para líneas de crédito de largo plazo.`, {
                                icon: '⚠️',
                                duration: 6000
                            });
                        }
                    } else {
                        console.error("Respuesta inesperada al obtener líneas:", data);
                        setLineas([]);
                    }
                })
                .catch(err => {
                    console.error("Error fetching habilitadas lines:", err);
                    setLineas([]);
                });
        }
    }, [socio, token, antiguedadSocio]);

    // Address Cascading
    useEffect(() => {
        if (addressForm.departamentoId && token) {
            fetch(`${API_BASE_URL}/ubicacion/ciudades/${addressForm.departamentoId}`, { headers: { 'Authorization': `Bearer ${token}` } })
                .then(res => res.json()).then(setCiudades).catch(console.error);
        } else {
            setCiudades([]);
        }
    }, [addressForm.departamentoId, token]);

    useEffect(() => {
        if (addressForm.ciudadId && token) {
            fetch(`${API_BASE_URL}/ubicacion/barrios/${addressForm.ciudadId}`, { headers: { 'Authorization': `Bearer ${token}` } })
                .then(res => res.json()).then(setBarrios).catch(console.error);
        } else {
            setBarrios([]);
        }
    }, [addressForm.ciudadId, token]);

    const handlePrepareUpdateAddress = () => {
        if (address) {
            setAddressForm({
                departamentoId: address.departamentoId || '',
                ciudadId: address.ciudadId || '',
                barrioId: address.barrioId || '',
                nroCasa: address.nroCasa || '',
                direccionExacta: address.direccionExacta || '',
                referencia: address.referencia || '',
                tipoDireccion: address.tipoDireccion || 'PARTICULAR'
            });
        }
        setAddress(null);
    };

    const handleAddressSave = async () => {
        if (!addressForm.barrioId || !addressForm.direccionExacta) {
            Swal.fire('Error', 'Complete Campos Obligatorios de Dirección', 'error');
            return;
        }
        try {
            const payload = { ...addressForm, socioId: socio.id, esPrincipal: true };
            const res = await fetch(`${API_BASE_URL}/socios/direccion`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify(payload)
            });
            if (res.ok) {
                const saved = await res.json();
                setAddress(saved);
                Swal.fire('Guardado', 'Dirección registrada', 'success');
            } else {
                Swal.fire('Error', 'No se pudo guardar la dirección', 'error');
            }
        } catch (e) {
            console.error(e);
        }
    };

    // --- CREDIT RULE LOGIC ---
    useEffect(() => {
        if (form.lineaCreditoId && token && socio) {
            setRegla(null);
            fetch(`${API_BASE_URL}/solicitudes/regla?lineaId=${form.lineaCreditoId}&monedaId=1&socioId=${socio.id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            })
                .then(res => res.ok ? res.json() : null)
                .then(data => {
                    setRegla(data);
                    // Aplicar límites iniciales de la regla
                    if (data) {
                        setForm(prev => ({
                            ...prev,
                            monto: prev.monto < data.montoDesde ? data.montoDesde : (prev.monto > data.montoHasta ? data.montoHasta : prev.monto),
                            plazo: data.plazosDisponibles?.length > 0 ? data.plazosDisponibles[0] : data.plazoMinimo
                        }));
                    }
                })
                .catch(() => setRegla(null));
        }
    }, [form.lineaCreditoId, token, socio]);

    const handleUpdateSocioProfile = async () => {
        if (!socio) return;
        setLoading(true);
        try {
            const getNumeric = (val) => {
                const parsed = parseInt(val);
                return isNaN(parsed) ? null : parsed;
            };

            const payload = {
                nombre: socio.personaFisica?.nombres || socio.nombre,
                apellido: socio.personaFisica?.apellidos || socio.apellido,
                numeroDocumento: socio.personaFisica?.cedula || socio.numeroDocumento,
                tipoPersonaId: socio.tipoPersona?.id,
                tipoSocioId: socio.tipoSocio?.id,
                tipoDocumentoId: socio.tipoDocumento?.id,
                fechaNacimiento: socio.personaFisica?.fechaNacimiento || socio.fechaNacimiento,
                generoId: socio.personaFisica?.genero?.id,
                estadoCivilId: socio.personaFisica?.estadoCivil?.id,
                nacionalidadId: socio.personaFisica?.nacionalidad?.id,
                contactos: [
                    { tipoContacto: 'CELULAR', valor: contactoForm.celular1, esPrincipal: true },
                    { tipoContacto: 'CELULAR', valor: contactoForm.celular2, esPrincipal: false },
                    { tipoContacto: 'EMAIL', valor: contactoForm.email1, esPrincipal: true },
                    { tipoContacto: 'EMAIL', valor: contactoForm.email2, esPrincipal: false }
                ].filter(c => c.valor && c.valor.trim() !== ''),
                // Support both existing and new address format
                departamentoId: getNumeric(address?.departamento?.id || address?.departamentoId || addressForm.departamentoId),
                ciudadId: getNumeric(address?.ciudad?.id || address?.ciudadId || addressForm.ciudadId),
                barrioId: getNumeric(address?.barrio?.id || address?.barrioId || addressForm.barrioId),
                nroCasa: address?.nroCasa || addressForm.nroCasa,
                direccionExacta: address?.direccionExacta || addressForm.direccionExacta,
                referencia: address?.referencia || addressForm.referencia,
                tipoDireccionId: getNumeric(address?.tipoDireccion?.id || address?.tipoDireccionId || 1),
                referencias: referencias.map(r => ({
                    nombre: r.nombre,
                    telefono: r.telefono,
                    tipoReferencia: r.tipoReferencia || r.tipo || 'PERSONAL'
                })),
                perfilEconomico: ingresos.map(i => ({
                    id: i.id,
                    tipoIngresoId: getNumeric(i.tipoIngresoId),
                    montoMensualPromedio: parseFloat(i.montoMensualPromedio) || 0,
                    descripcionFuente: i.descripcionFuente,
                    promedioUltimos6Meses: parseFloat(i.promedioUltimos6Meses) || null,
                    verificadoPortal: i.verificadoPortal || false,
                    esActual: i.esActual ?? true
                })),
                datosLaborales: {
                    empresa: laboralForm.empresa,
                    profesionId: getNumeric(laboralForm.profesionId),
                    rangoAntiguedadId: getNumeric(laboralForm.rangoAntiguedadId),
                    tipoOcupacion: laboralForm.tipoOcupacion,
                    direccion: laboralForm.direccion,
                    telefono: laboralForm.telefono
                }
            };

            const formData = new FormData();
            formData.append('socio', new Blob([JSON.stringify(payload)], { type: 'application/json' }));

            if (fileIngreso) {
                formData.append('comprobanteIngreso', fileIngreso);
            }

            const res = await fetch(`${API_BASE_URL}/socios/${socio.id}/completo`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });

            if (!res.ok) {
                const errorText = await res.text();
                throw new Error(errorText || "Error al actualizar perfil del socio");
            }

            toast.success("Perfil del socio actualizado correctamente");
            return true;
        } catch (e) {
            console.error(e);
            toast.error("No se pudo actualizar el perfil maestro");
            return false;
        } finally {
            setLoading(false);
        }
    };

    const handleAddIngreso = () => {
        setIngresos([...ingresos, {
            tipoIngresoId: '',
            montoMensualPromedio: 0,
            descripcionFuente: '',
            promedioUltimos6Meses: '',
            verificadoPortal: false,
            esActual: true,
            empresa: '',
            profesionId: '',
            direccion: '',
            telefono: ''
        }]);
    };

    const handleUpdateIngreso = (index, field, value) => {
        const newIngresos = [...ingresos];
        newIngresos[index][field] = value;
        setIngresos(newIngresos);
    };

    const handleRemoveIngreso = (index) => {
        setIngresos(ingresos.filter((_, i) => i !== index));
    };

    // --- WIZARD NAVIGATION ---
    const nextStep = async () => {
        if (step === 1) {
            // Validate Step 1
            if (!socio) return Swal.fire('Error', 'Seleccione un Socio', 'warning');
            if (!verificado) return Swal.fire('Atención', 'Debe confirmar la veracidad de los datos antes de continuar', 'info');

            // Persistence: Update Master Profile automatically
            const success = await handleUpdateSocioProfile();
            if (success) {
                // REFRESH PERFIL 360 to reflect new incomes in sidebar
                await cargarPerfil360(socio.id);
                setStep(2);
            }
        } else if (step === 2) {
            // Estructura & Garantía
            setStep(3);
        } else if (step === 3) {
            // Referencias & Contacto - SAVE PROFILE FIRST
            if (!form.lineaCreditoId || !form.monto || !form.plazo || !form.destinoId) {
                return Swal.fire('Error', 'Complete los datos de la operación y el destino del crédito', 'warning');
            }
            if (referencias.length < 2) {
                return Swal.fire('Error', 'Debe agregar al menos 2 referencias personales antes de avanzar al cierre', 'warning');
            }

            // Persistence: Update Master Profile automatically before validation
            const success = await handleUpdateSocioProfile();
            if (!success) return;

            // REFRESH PERFIL 360
            await cargarPerfil360(socio.id);

            // Call Validation Endpoint (Motor de Riesgo)
            setLoading(true);
            try {
                const res = await fetch(`${API_BASE_URL}/solicitudes/validar-requisitos?socioId=${socio.id}&lineaId=${form.lineaCreditoId}&monto=${form.monto}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const data = await res.json();
                if (data.status === 'ERROR') {
                    throw new Error(data.message);
                }
                setStep(4);
            } catch (e) {
                Swal.fire('Requisitos No Cumplidos', e.message, 'error');
            } finally {
                setLoading(false);
            }
        }
    };

    const prevStep = () => setStep(step - 1);

    // --- HANDLERS ---
    const handleCameraStart = (type) => {
        setActiveDocType(type);
        setShowCamera(true);
        navigator.mediaDevices.getUserMedia({ video: true })
            .then(stream => {
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                    videoRef.current.play();
                }
            })
            .catch(err => Swal.fire('Error Cámara', 'No se pudo acceder a la cámara' + err, 'error'));
    };

    const handleCapture = () => {
        const context = canvasRef.current.getContext('2d');
        context.drawImage(videoRef.current, 0, 0, 640, 480);
        const dataUrl = canvasRef.current.toDataURL('image/jpeg');

        // Stop stream
        const stream = videoRef.current.srcObject;
        const tracks = stream.getTracks();
        tracks.forEach(track => track.stop());
        setShowCamera(false);

        // Save
        setDocumentos([...documentos, { tipo: activeDocType, nombre: `Foto_${activeDocType}.jpg`, file: dataUrl, isBase64: true }]);
    };

    const handleFileUpload = (e, type) => {
        const file = e.target.files[0];
        if (file) {
            // Convert to Base64 for consistency or handle as file
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                setDocumentos([...documentos, { tipo: type, nombre: file.name, file: reader.result, isBase64: true }]);
            };
        }
    };

    const handleSocioSeleccionado = async (s) => {
        // EARLY VALIDATION: Call /validar-socio before setting the socio
        try {
            const valRes = await fetch(`${API_BASE_URL}/solicitudes/validar-socio?socioId=${s.id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const valData = await valRes.json();

            // Store Viabilidad Data for UI display
            setViabilidadData(valData);

            if (valData.status === 'ERROR' && valData.errores && valData.errores.length > 0) {
                // If there's a critical error (like pending solicitud or inactive), 
                // we still set the socio but block progress in the UI
                Swal.fire({
                    title: 'Restricción de Proceso',
                    text: valData.errores[0] || 'El socio tiene impedimentos para un nuevo crédito.',
                    icon: 'warning',
                    confirmButtonColor: '#0f8c79'
                });
            }

            // Store Viabilidad Data for Badges
            setViabilidadData(valData);

            if (valData.antiguedadMeses !== undefined) {
                setAntiguedadSocio(valData.antiguedadMeses);
            }

            // Show warnings as toast if any
            if (valData.advertencias && valData.advertencias.length > 0) {
                valData.advertencias.forEach(w => toast(w, { icon: 'ℹ️', duration: 4000 }));
            }
        } catch (e) {
            console.error('Error validando socio:', e);
            // Don't block on network errors, just warn
            toast.error('No se pudo validar al socio. Verifique manualmente.');
        }

        setSocio(s);
        cargarPerfil360(s.id);
        setVerificado(false); // Reset verification on new socio

        // AUTO-COMPLETION: Contact Data
        const celulares = s.contactos?.filter(c => c.tipoContacto === 'CELULAR' || c.tipo === 'CELULAR') || [];
        const emails = s.contactos?.filter(c => c.tipoContacto === 'EMAIL' || c.tipo === 'EMAIL') || [];

        setContactoForm({
            celular1: celulares[0]?.valor || '',
            celular2: celulares[1]?.valor || '',
            email1: emails[0]?.valor || '',
            email2: emails[1]?.valor || ''
        });

        // AUTO-COMPLETION: Laboral
        if (s.datosLaborales) {
            setLaboralForm({
                empresa: s.datosLaborales.empresa || '',
                profesionId: s.datosLaborales.profesion?.id || '',
                rangoAntiguedadId: s.datosLaborales.rangoAntiguedad?.id || '',
                tipoOcupacion: s.datosLaborales.tipoOcupacion || 'DEPENDIENTE',
                direccion: s.datosLaborales.direccion || '',
                telefono: s.datosLaborales.telefono || ''
            });
        } else {
            setLaboralForm({ empresa: '', profesionId: '', rangoAntiguedadId: '', tipoOcupacion: 'DEPENDIENTE', direccion: '', telefono: '' });
        }

        // AUTO-COMPLETION: Ingresos
        if (s.perfilEconomico && s.perfilEconomico.length > 0) {
            setIngresos(s.perfilEconomico.map(pe => ({
                id: pe.id,
                tipoIngresoId: pe.tipoIngreso?.id || '',
                montoMensualPromedio: pe.montoMensualPromedio || 0,
                descripcionFuente: pe.descripcionFuente || '',
                promedioUltimos6Meses: pe.promedioUltimos6Meses || '',
                verificadoPortal: pe.verificadoPortal || false,
                esActual: pe.esActual,
                empresa: pe.empresa || '',
                profesionId: pe.profesion?.id || '',
                direccion: pe.direccion || '',
                telefono: pe.telefono || ''
            })));
        } else {
            setIngresos([]);
        }

        // AUTO-COMPLETION: Servicios
        if (s.servicios && s.servicios.length > 0) {
            setServicios(s.servicios.map(ser => ({
                tipoServicio: ser.tipoServicio,
                identificadorServicio: ser.identificadorServicio,
                titularNombre: ser.titularNombre,
                latitud: ser.latitud,
                longitud: ser.longitud
            })));
        } else {
            setServicios([]);
        }

        // AUTO-COMPLETION: Direccion
        if (token) {
            try {
                const res = await fetch(`${API_BASE_URL}/socios/${s.id}/direccion`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    if (data && data.id) {
                        setAddress(data);
                    } else {
                        setAddress(null);
                    }
                }
            } catch (e) { console.error("Error auto-completing address:", e); }
        }
    };

    const cargarPerfil360 = async (id) => {
        setLoadingPerfil(true);
        try {
            const response = await fetch(`${API_BASE_URL}/socios/${id}/perfil-360`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setPerfil360(data);
            } else {
                setError("No se pudo cargar el perfil 360.");
            }
        } catch (err) {
            console.error("Error cargando perfil 360:", err);
            setError("Error de red al cargar el perfil 360.");
        } finally {
            setLoadingPerfil(false);
        }
    };

    const handleSimular = async () => {
        setLoading(true);
        try {
            const payload = {
                socioId: socio.id,
                lineaCreditoId: parseInt(form.lineaCreditoId) || 0,
                monedaId: 1,
                monto: parseFloat(form.monto) || 0,
                plazo: parseInt(form.plazo) || 0,
                tna: regla?.tasaCompensatoria || regla?.tasaInteres || 0,
                fechaPrimerVencimiento: form.fechaPrimerVencimiento && form.fechaPrimerVencimiento.trim() !== "" ? form.fechaPrimerVencimiento : null,
                metodoAmortizacionId: 1
            };
            console.log("%cPayload enviado a simular:", "color: blue; font-weight: bold;", payload);

            const res = await fetch(`${API_BASE_URL}/solicitudes/simular`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify(payload)
            });
            if (res.ok) {
                const data = await res.json();
                setSimulacion(data.tablaAmortizacion);
                setShowSimulateModal(true);
                setModalPage(1);
            } else {
                let errorMsg = 'El servidor rechazó la solicitud (400)';
                const errorText = await res.text();
                console.error("%cError del Servidor (Cuerpo):", "color: red; font-weight: bold;", errorText);

                try {
                    const errorData = JSON.parse(errorText);
                    errorMsg = errorData.message || errorMsg;
                } catch (e) {
                    errorMsg = errorText || errorMsg;
                }

                Swal.fire({
                    icon: 'error',
                    title: 'Error en Simulación',
                    text: errorMsg
                });
            }
        } catch (e) {
            console.error(e);
            Swal.fire('Error', 'No se pudo conectar con el servidor para simular', 'error');
        }
        setLoading(false);
    };

    const handleFinalSubmit = async () => {
        setLoading(true);
        try {
            // Safe documentos mapping - skip items without valid base64 data
            const mappedDocs = documentos
                .filter(d => d.file && typeof d.file === 'string' && d.file.includes(','))
                .map(d => ({
                    tipoDocumentoId: d.tipo === 'INGRESOS' ? 2 : 3,
                    nombreArchivo: d.nombre,
                    contenidoBase64: d.file.split(',')[1]
                }));

            const payload = {
                socioId: socio.id,
                lineaCreditoId: parseInt(form.lineaCreditoId) || 0,
                monedaId: 1,
                monto: parseFloat(form.monto) || 0,
                plazo: parseInt(form.plazo) || 0,
                fechaPrimerVencimiento: form.fechaPrimerVencimiento && form.fechaPrimerVencimiento.trim() !== "" ? form.fechaPrimerVencimiento : null,
                destinoId: parseInt(form.destinoId) || null,
                metodoAmortizacionId: 1,
                observacion: form.observacion || '',
                planPagos: simulacion,
                referencias: referencias.map(r => ({ nombre: r.nombre, telefono: r.telefono, tipoReferencia: r.tipo || 'PERSONAL' })),
                documentos: mappedDocs,
                servicios: servicios
            };

            console.log("%c=== PAYLOAD CREAR SOLICITUD ===", "color: green; font-weight: bold; font-size: 14px;");
            console.log("socioId:", payload.socioId);
            console.log("lineaCreditoId:", payload.lineaCreditoId);
            console.log("monto:", payload.monto);
            console.log("plazo:", payload.plazo);
            console.log("destinoId:", payload.destinoId);
            console.log("fechaPrimerVencimiento:", payload.fechaPrimerVencimiento);
            console.log("referencias:", payload.referencias);
            console.log("documentos count:", payload.documentos.length);
            console.log("planPagos count:", payload.planPagos?.length);
            console.log("Full payload:", JSON.stringify(payload).substring(0, 2000));

            const response = await fetch(`${API_BASE_URL}/solicitudes/crear`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                let errorMsg = `Error del servidor (${response.status})`;
                try {
                    const errBody = await response.text();
                    try {
                        const errJson = JSON.parse(errBody);
                        // Solo usamos el mensaje amigable si existe
                        errorMsg = errJson.message || errorMsg;
                    } catch (e) {
                        errorMsg = errBody || errorMsg;
                    }
                } catch (e) {
                    console.error("No se pudo leer el cuerpo del error:", e);
                }
                throw new Error(errorMsg);
            }

            const responseData = await response.json();
            setSavedSolicitudId(responseData.id);

            if (step === 4) {
                Swal.fire('Éxito', 'Solicitud finalizada correctamente', 'success');
                navigate('/socios');
            }
            return responseData.id;

        } catch (e) {
            console.error("handleFinalSubmit error:", e);
            Swal.fire('Error', e.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 font-sans text-slate-800 p-6 md:p-12 relative">
            <Toaster position="top-right" />

            {/* AMBIENT BACKGROUND GLOW */}
            <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-[#0f8c79]/10 blur-[120px] rounded-full pointer-events-none"></div>

            <div className="max-w-[1000px] mx-auto bg-white rounded-3xl border border-[#0f8c79] shadow-xl relative overflow-hidden z-10 flex flex-col min-h-[800px]">

                <div className="p-10 md:p-14 flex-1">
                    {/* HEADER TITLE */}
                    <div className="text-center mb-10">
                        <h1 className="text-2xl font-bold text-[#2d4a5d] mb-4">Solicitud de crédito</h1>
                        <p className="text-[13px] text-slate-600 max-w-4xl mx-auto leading-relaxed">
                            Las solicitudes de créditos pueden ser hasta el monto de Gs. 100.000.000 incluidos los saldos de créditos vigentes, y los montos de líneas de tarjetas de créditos.
                        </p>
                    </div>

                    {/* DATOS DEL SOLICITANTE - WIREFRAME STYLE */}
                    {socio && (
                        <div className="mb-8 font-sans max-w-4xl mx-auto">
                            <div className="flex justify-between items-end border-b-2 border-slate-800 pb-2 mb-2">
                                <h2 className="text-[13px] font-bold text-slate-800 uppercase tracking-widest">Datos del Solicitante</h2>
                                <button onClick={() => { setSocio(null); setViabilidadData(null); setStep(1); }} className="text-[11px] font-bold text-[#0f8c79] hover:text-[#0c7363] uppercase flex items-center gap-1 transition-colors">
                                    <RefreshCw size={12} /> Cambiar Socio
                                </button>
                            </div>
                            <div className="bg-white border border-slate-200 p-4 shadow-sm">
                                {/* ROW 1 */}
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-[12px] border-b border-slate-100 pb-3 mb-3">
                                    <div className="md:col-span-1 border-r border-slate-100 md:pr-4">
                                        <span className="text-slate-500 block text-[10px] uppercase mb-0.5">Socio</span>
                                        <span className="font-bold text-slate-900 truncate block" title={`${socio.nombres} ${socio.apellidos}`}>{socio.nombres} {socio.apellidos}</span>
                                    </div>
                                    <div className="md:col-span-1 border-r border-slate-100 md:pr-4 md:pl-2">
                                        <span className="text-slate-500 block text-[10px] uppercase mb-0.5">C.I.</span>
                                        <span className="font-bold text-slate-900">{new Intl.NumberFormat('es-PY').format(socio.numeroDocumento || 0)}</span>
                                    </div>
                                    <div className="md:col-span-1 border-r border-slate-100 md:pr-4 md:pl-2">
                                        <span className="text-slate-500 block text-[10px] uppercase mb-0.5">Nro. de Socio</span>
                                        <span className="font-bold text-slate-900">{socio.id}</span>
                                    </div>
                                    <div className="md:col-span-1 md:pl-2">
                                        <span className="text-slate-500 block text-[10px] uppercase mb-0.5">Sucursal</span>
                                        <span className="font-bold text-slate-900">CENTRAL</span>
                                    </div>
                                </div>
                                {/* ROW 2 */}
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-[12px]">
                                    <div className="md:col-span-1 border-r border-slate-100 md:pr-4">
                                        <span className="text-slate-500 block text-[10px] uppercase mb-0.5">Antigüedad</span>
                                        <span className="font-bold text-slate-900">{viabilidadData?.antiguedadMeses || 0} Meses</span>
                                    </div>
                                    <div className="md:col-span-1 border-r border-slate-100 md:pr-4 md:pl-2">
                                        <span className="text-slate-500 block text-[10px] uppercase mb-0.5">Categoría</span>
                                        <span className="font-bold text-[#0f8c79]">{socio.categoria?.descripcion || 'A - EXCELENTE'}</span>
                                    </div>
                                    <div className="md:col-span-1 border-r border-slate-100 md:pr-4 md:pl-2">
                                        <span className="text-slate-500 block text-[10px] uppercase mb-0.5">Créditos Anteriores</span>
                                        <span className="font-bold text-slate-900">{viabilidadData?.cantidadCreditos || 0}</span>
                                    </div>
                                    <div className="md:col-span-1 md:pl-2">
                                        <span className="text-slate-500 block text-[10px] uppercase mb-0.5">Estado</span>
                                        <span className="font-bold text-slate-900">{socio.estado?.descripcion || 'ACTIVO'}</span>
                                    </div>
                                </div>
                                {viabilidadData?.listaCreditos && viabilidadData.listaCreditos.length > 0 && (
                                    <div className="mt-4 p-4 bg-slate-50 rounded-2xl border border-slate-100">
                                        <div className="flex flex-col gap-4">
                                            {/* ALERTA DE SOLICITUD PENDIENTE */}
                                            {viabilidadData?.solicitudPendiente && (
                                                <div className="bg-red-50 border-2 border-red-200 p-5 rounded-2xl flex flex-col md:flex-row items-center gap-6 animate-pulse">
                                                    <div className="bg-red-500 text-white p-3 rounded-full shadow-lg">
                                                        <AlertCircle size={32} />
                                                    </div>
                                                    <div className="flex-1">
                                                        <div className="flex justify-between items-center mb-2">
                                                            <h4 className="text-red-800 font-black text-sm uppercase tracking-tight">Bloqueo Operativo: Solicitud Detectada</h4>
                                                            <span className="bg-red-600 text-white px-3 py-1 rounded-full text-[10px] font-black uppercase">Estado: {viabilidadData.solicitudPendienteEstado}</span>
                                                        </div>
                                                        <p className="text-red-700 text-xs mb-4 leading-relaxed">
                                                            El socio ya posee una solicitud activa en el sistema. No se permite el ingreso de nuevas solicitudes paralelas para el mismo socio.
                                                        </p>
                                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-white/50 p-3 rounded-xl border border-red-100">
                                                            <div>
                                                                <span className="text-[9px] text-red-400 font-black uppercase block">ID Solicitud</span>
                                                                <span className="text-xs font-black text-red-900">#{viabilidadData.solicitudPendiente}</span>
                                                            </div>
                                                            <div>
                                                                <span className="text-[9px] text-red-400 font-black uppercase block">Monto</span>
                                                                <span className="text-xs font-black text-red-900 font-mono">Gs. {new Intl.NumberFormat('es-PY').format(viabilidadData.solicitudPendienteMonto)}</span>
                                                            </div>
                                                            <div>
                                                                <span className="text-[9px] text-red-400 font-black uppercase block">Plazo</span>
                                                                <span className="text-xs font-black text-red-900">{viabilidadData.solicitudPendientePlazo} Cuotas</span>
                                                            </div>
                                                            <div>
                                                                <span className="text-[9px] text-red-400 font-black uppercase block">Fecha</span>
                                                                <span className="text-xs font-black text-red-900">{viabilidadData.solicitudPendienteFecha}</span>
                                                            </div>
                                                        </div>
                                                        <div className="mt-3 text-[10px] text-red-500 font-bold italic">
                                                            Línea: {viabilidadData.solicitudPendienteLinea}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            <div className="mt-2 overflow-hidden border border-slate-200 rounded-xl">
                                                <table className="w-full text-left border-collapse bg-white">
                                                    <thead>
                                                        <tr className="bg-slate-50 border-b border-slate-200">
                                                            <th className="px-4 py-3 text-[10px] font-black text-slate-500 uppercase tracking-widest">Nro. Credito</th>
                                                            <th className="px-4 py-3 text-[10px] font-black text-slate-500 uppercase tracking-widest">Estado</th>
                                                            <th className="px-4 py-3 text-[10px] font-black text-slate-500 uppercase tracking-widest text-center">Cuota</th>
                                                            <th className="px-4 py-3 text-[10px] font-black text-slate-500 uppercase tracking-widest text-right">Saldo Capital</th>
                                                            <th className="px-4 py-3 text-[10px] font-black text-slate-500 uppercase tracking-widest text-right">Monto Original</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-slate-100">
                                                        {viabilidadData.listaCreditos.map((cred, idx) => (
                                                            <tr key={idx} className="hover:bg-slate-50/50 transition-colors">
                                                                <td className="px-4 py-3">
                                                                    <div className="text-xs font-black text-slate-800">#{cred.id}</div>
                                                                    <div className="text-[9px] text-slate-400 font-mono">{cred.nroOperacion || 'N/A'}</div>
                                                                </td>
                                                                <td className="px-4 py-3">
                                                                    <span className={`text-[9px] font-black px-2 py-0.5 rounded-full uppercase ${cred.estadoValor === 'DES' ? 'bg-emerald-100 text-emerald-600' :
                                                                        cred.estadoValor === 'CAN' ? 'bg-slate-100 text-slate-400' :
                                                                            'bg-amber-100 text-amber-600'
                                                                        }`}>
                                                                        {cred.estado}
                                                                    </span>
                                                                </td>
                                                                <td className="px-4 py-3">
                                                                    <div className="flex flex-col items-center">
                                                                        <div className="text-[10px] font-black text-slate-700">{cred.cuotasPagadas} / {cred.totalCuotas}</div>
                                                                        <div className="w-20 h-1 bg-slate-100 rounded-full mt-1 overflow-hidden">
                                                                            <div
                                                                                className={`h-full ${cred.estadoValor === 'CAN' ? 'bg-slate-300' : 'bg-emerald-500'}`}
                                                                                style={{ width: `${(cred.cuotasPagadas / cred.totalCuotas) * 100}%` }}
                                                                            ></div>
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                                <td className="px-4 py-3 text-right">
                                                                    <span className="text-xs font-black text-slate-900 font-mono">
                                                                        {new Intl.NumberFormat('es-PY').format(cred.saldoCapitalPendiente || 0)}
                                                                    </span>
                                                                </td>
                                                                <td className="px-4 py-3 text-right">
                                                                    <span className="text-xs font-bold text-slate-400 font-mono">
                                                                        {new Intl.NumberFormat('es-PY').format(cred.montoOriginal || 0)}
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                )}


                            </div>
                            <div className="border-t-2 border-slate-800 mt-2"></div>
                        </div>
                    )}

                    {/* CLASSIC TEXT STEPPER */}
                    <div className="flex flex-wrap items-center justify-center gap-2 md:gap-6 mb-12 border-b border-slate-100 pb-8">
                        {[
                            { id: 1, title: 'Datos Personales' },
                            { id: 2, title: 'Crédito' },
                            { id: 3, title: 'Garantías' },
                            { id: 4, title: 'Documentos' }
                        ].map((s) => {
                            const active = step === s.id;
                            return (
                                <button
                                    key={s.id}
                                    onClick={() => socio && setStep(s.id)}
                                    disabled={!socio && s.id > 1}
                                    className={`px-6 py-2 rounded-full text-[13px] font-bold transition-all ${active
                                        ? 'bg-[#0f8c79] text-white shadow-md'
                                        : 'text-slate-400 hover:text-slate-600'
                                        }`}
                                >
                                    {s.id}. {s.title}
                                </button>
                            );
                        })}
                    </div>

                    {/* SCROLLABLE STEP CONTENT */}
                    <div className="flex-1 overflow-y-auto p-6 md:p-12">
                        <div className="max-w-7xl mx-auto pb-64">
                            <>

                                {/* STEP 1: DATOS PERSONALES */}
                                {step === 1 && (
                                    <div className="space-y-8 animate-fade-in">
                                        {!socio && (
                                            <div className="max-w-xl mx-auto py-12 text-center">
                                                <h2 className="text-xl font-bold text-[#2d4a5d] mb-6">Identificación del Solicitante</h2>
                                                <div className="border border-gray-200 rounded-xl p-2 bg-gray-50">
                                                    <SocioSearch
                                                        onSocioSelect={handleSocioSeleccionado}
                                                        compact={true}
                                                        showHeaderLabel={false}
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        {socio && (
                                            <>
                                                {/* CHECKLIST DE VIABILIDAD */}
                                                <div className="mb-10">
                                                    <h3 className="text-lg font-bold text-[#2d4a5d] mb-4">Checklist de Viabilidad</h3>
                                                    <div className="border border-gray-200 rounded-md overflow-hidden bg-white">
                                                        <table className="w-full text-sm text-left">
                                                            <thead className="bg-gray-50 text-gray-600 font-bold border-b border-gray-200 uppercase text-xs tracking-wider">
                                                                <tr>
                                                                    <th className="py-3 px-4">Validación</th>
                                                                    <th className="py-3 px-4">Estado</th>
                                                                    <th className="py-3 px-4">Detalle</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                <tr className="border-b border-gray-100">
                                                                    <td className="py-3 px-4 font-bold text-gray-700">Antigüedad</td>
                                                                    <td className="py-3 px-4">
                                                                        <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-bold">✓ APTO</span>
                                                                    </td>
                                                                    <td className="py-3 px-4 text-gray-600">Tiene {viabilidadData?.antiguedadMeses || 0} meses como socio</td>
                                                                </tr>
                                                                <tr className="border-b border-gray-100">
                                                                    <td className="py-3 px-4 font-bold text-gray-700">Aportes y Solidaridad</td>
                                                                    <td className="py-3 px-4">
                                                                        <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-bold">✓ AL DÍA</span>
                                                                    </td>
                                                                    <td className="py-3 px-4 text-gray-600">Saldo suficiente y sin atrasos</td>
                                                                </tr>
                                                                <tr className="border-b border-gray-100">
                                                                    <td className="py-3 px-4 font-bold text-gray-700">Historial Crediticio</td>
                                                                    <td className="py-3 px-4">
                                                                        {viabilidadData?.tieneMora ? (
                                                                            <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-bold">✗ MORA</span>
                                                                        ) : (
                                                                            <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-bold">✓ EXCELENTE</span>
                                                                        )}
                                                                    </td>
                                                                    <td className="py-3 px-4 text-gray-600">
                                                                        {viabilidadData?.tieneMora ? "Registra mora activa." : "Al día con todos sus compromisos."}
                                                                    </td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="py-3 px-4 font-bold text-gray-700">Documentación (KYC)</td>
                                                                    <td className="py-3 px-4">
                                                                        {viabilidadData?.kycDesactualizado ? (
                                                                            <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded text-xs font-bold">! ACTUALIZAR</span>
                                                                        ) : (
                                                                            <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-bold">✓ VIGENTE</span>
                                                                        )}
                                                                    </td>
                                                                    <td className="py-3 px-4 text-gray-600 flex justify-between items-center">
                                                                        <span>{viabilidadData?.kycDesactualizado ? "Requiere actualización de datos" : "Legajo completo"}</span>
                                                                        {viabilidadData?.kycDesactualizado && (
                                                                            <button onClick={handleUpdateSocioProfile} className="text-[#0f8c79] hover:underline text-xs font-bold">Actualizar Ahora</button>
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                    {(viabilidadData?.tieneMora || viabilidadData?.kycDesactualizado || viabilidadData?.solicitudPendiente) && (
                                                        <div className="mt-4 p-4 bg-orange-50 border-l-4 border-orange-500 rounded-r-xl">
                                                            <div className="flex gap-3">
                                                                <AlertCircle className="text-orange-600 flex-shrink-0" size={20} />
                                                                <div>
                                                                    <p className="text-sm font-bold text-orange-900">Bloqueo Operativo Detectado</p>
                                                                    <ul className="text-xs text-orange-800 mt-1 list-disc list-inside space-y-1">
                                                                        {viabilidadData.tieneMora && <li>El socio registra mora activa en sus obligaciones.</li>}
                                                                        {viabilidadData.kycDesactualizado && <li>El legajo del socio está desactualizado (KYC).</li>}
                                                                        {viabilidadData.solicitudPendiente && (
                                                                            <li>
                                                                                Ya existe una solicitud <span className="font-bold">PENDIENTE</span> (Nro: {viabilidadData.solicitudPendiente},
                                                                                Monto: {new Intl.NumberFormat('es-PY').format(viabilidadData.solicitudPendienteMonto)} Gs).
                                                                            </li>
                                                                        )}
                                                                        {viabilidadData?.status === 'ERROR' && viabilidadData?.errores?.map((err, i) => (
                                                                            <li key={i}>{err}</li>
                                                                        ))}
                                                                        {socio.estado?.id !== 1 && !viabilidadData?.errores?.some(e => e.includes('estado')) && <li>El socio no está en estado ACTIVO.</li>}
                                                                    </ul>
                                                                    <p className="text-[10px] text-orange-600 mt-2 font-medium italic">* No podrá avanzar hasta que estas condiciones sean regularizadas.</p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="pt-8 flex justify-end">
                                                    <button
                                                        onClick={() => {
                                                            const isBlocked = viabilidadData?.status === 'ERROR' ||
                                                                viabilidadData?.tieneMora ||
                                                                viabilidadData?.kycDesactualizado ||
                                                                viabilidadData?.solicitudPendiente;
                                                                
                                                            if (isBlocked) {
                                                                const errorMsg = viabilidadData?.errores?.[0] || 'El socio no califica para crédito en este momento.';
                                                                Swal.fire('Bloqueo Operativo', errorMsg, 'error');
                                                                return;
                                                            }
                                                            setStep(2);
                                                        }}
                                                        disabled={viabilidadData?.status === 'ERROR' || viabilidadData?.tieneMora || viabilidadData?.kycDesactualizado || viabilidadData?.solicitudPendiente}
                                                        className={`px-8 py-2.5 rounded-full text-sm font-bold transition-all ${(viabilidadData?.status === 'ERROR' || viabilidadData?.tieneMora || viabilidadData?.kycDesactualizado || viabilidadData?.solicitudPendiente)
                                                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                            : 'bg-[#0f8c79] text-white hover:bg-[#0c7363] shadow-md'
                                                            }`}
                                                    >
                                                        Siguiente
                                                    </button>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}

                                {/* STEP 2: ESTRUCTURA DEL CRÉDITO */}
                                {step === 2 && socio && (
                                    <div className="space-y-8 animate-fade-in">
                                        <div className="space-y-6">
                                            <h3 className="text-lg font-bold text-[#2d4a5d] mb-6">Estructura del Crédito</h3>

                                            {/* TIPO DE GARANTÍA */}
                                            <div>
                                                <p className="text-sm font-bold text-gray-700 mb-3">Tipo de Garantía</p>
                                                <div className="flex items-center gap-6">
                                                    <label className="flex items-center gap-2 cursor-pointer">
                                                        <input type="radio" name="garantia" value="A sola firma" checked={form.garantia === 'A sola firma'} onChange={e => setForm({ ...form, garantia: e.target.value })} className="w-4 h-4 text-[#0f8c79] border-gray-300 focus:ring-[#0f8c79]" />
                                                        <span className="text-sm text-gray-600">A sola firma</span>
                                                    </label>
                                                    <label className="flex items-center gap-2 cursor-pointer">
                                                        <input type="radio" name="garantia" value="Codeudor" checked={form.garantia === 'Codeudor'} onChange={e => setForm({ ...form, garantia: e.target.value })} className="w-4 h-4 text-[#0f8c79] border-gray-300 focus:ring-[#0f8c79]" />
                                                        <span className="text-sm text-gray-600">Codeudor</span>
                                                    </label>
                                                </div>
                                            </div>

                                            {/* IMPORTE SOLICITADO */}
                                            <div className="w-full md:w-1/3 mt-2">
                                                <input
                                                    type="text"
                                                    className="w-full rounded-md border border-gray-300 p-2.5 text-sm bg-white focus:outline-none focus:border-[#0f8c79] focus:ring-1 focus:ring-[#0f8c79]"
                                                    value={form.monto ? new Intl.NumberFormat('es-PY').format(form.monto) : ''}
                                                    onChange={e => {
                                                        const rawValue = e.target.value.replace(/\D/g, '');
                                                        const numericValue = parseInt(rawValue) || 0;
                                                        setForm({ ...form, monto: numericValue });
                                                    }}
                                                    placeholder="Importe Solicitado *"
                                                />
                                                {form.monto > 0 && viabilidadData && (
                                                    <div className="mt-2">
                                                        {(() => {
                                                            const porcentaje = viabilidadData.porcentajeCoberturaAportes || 10;
                                                            const requerido = (form.monto * porcentaje) / 100;
                                                            const saldo = viabilidadData.saldoAportes || 0;
                                                            const cumple = saldo >= requerido;

                                                            return (
                                                                <div className={`p-3 rounded-lg border flex items-center gap-3 ${cumple ? 'bg-emerald-50 border-emerald-100 text-emerald-700' : 'bg-red-50 border-red-100 text-red-700'}`}>
                                                                    {cumple ? <ShieldCheck size={16} /> : <AlertCircle size={16} />}
                                                                    <div className="text-[11px] leading-tight">
                                                                        <p className="font-bold">Cobertura de Aportes ({porcentaje}%)</p>
                                                                        <p>Saldo Actual: <span className="font-mono">Gs. {new Intl.NumberFormat('es-PY').format(saldo)}</span></p>
                                                                        {!cumple && <p className="font-black mt-1">Mínimo Requerido: Gs. {new Intl.NumberFormat('es-PY').format(requerido)}</p>}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })()}
                                                    </div>
                                                )}
                                            </div>

                                            {/* FORMA DE PAGO */}
                                            <div className="mt-6">
                                                <p className="text-sm font-bold text-gray-700 mb-3">Forma de Pago</p>
                                                <div className="flex flex-col gap-3">
                                                    <label className="flex items-center gap-2 cursor-pointer">
                                                        <input type="radio" name="formaPago" value="Débito automático" checked={form.formaPago === 'Débito automático'} onChange={e => setForm({ ...form, formaPago: e.target.value })} className="w-4 h-4 text-[#0f8c79] border-gray-300 focus:ring-[#0f8c79]" />
                                                        <span className="text-sm text-gray-600">Débito automático</span>
                                                    </label>
                                                    <label className="flex items-center gap-2 cursor-pointer">
                                                        <input type="radio" name="formaPago" value="Caja" checked={form.formaPago === 'Caja'} onChange={e => setForm({ ...form, formaPago: e.target.value })} className="w-4 h-4 text-[#0f8c79] border-gray-300 focus:ring-[#0f8c79]" />
                                                        <span className="text-sm text-gray-600">Caja</span>
                                                    </label>
                                                </div>
                                            </div>

                                            {/* GRID DATOS OPERATIVOS */}
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                                                <div>
                                                    <label className="block text-[10px] uppercase font-bold text-gray-500 mb-1">Línea de Crédito *</label>
                                                    <select className="w-full rounded-md border border-gray-300 p-2.5 text-sm bg-white focus:outline-none focus:border-[#0f8c79] focus:ring-1 focus:ring-[#0f8c79] text-gray-600" value={form.lineaCreditoId} onChange={e => setForm({ ...form, lineaCreditoId: e.target.value })}>
                                                        <option value="">Línea de Crédito *</option>
                                                        {lineas.map(l => <option key={l.id} value={l.id}>{l.nombre}</option>)}
                                                    </select>
                                                </div>

                                                <div>
                                                    <label className="block text-[10px] uppercase font-bold text-gray-500 mb-1">Plazo (Meses) *</label>
                                                    <select className="w-full rounded-md border border-gray-300 p-2.5 text-sm bg-white focus:outline-none focus:border-[#0f8c79] focus:ring-1 focus:ring-[#0f8c79] text-gray-600" value={form.plazo} onChange={e => setForm({ ...form, plazo: parseInt(e.target.value) || 0 })}>
                                                        <option value="">Plazo (Meses) *</option>
                                                        {regla && Array.from(
                                                            { length: (regla.plazoIncremento > 0) ? Math.floor((regla.plazoMaximo - regla.plazoMinimo) / regla.plazoIncremento) + 1 : (regla.plazoMaximo - regla.plazoMinimo + 1) },
                                                            (_, i) => regla.plazoMinimo + (i * (regla.plazoIncremento || 1))
                                                        ).map(p => (
                                                            <option key={p} value={p}>{p} Meses</option>
                                                        ))}
                                                    </select>
                                                </div>

                                                <div>
                                                    <label className="block text-[10px] uppercase font-bold text-gray-500 mb-1">Destino del Crédito *</label>
                                                    <select className="w-full rounded-md border border-gray-300 p-2.5 text-sm bg-white focus:outline-none focus:border-[#0f8c79] focus:ring-1 focus:ring-[#0f8c79] text-gray-600" value={form.destinoId} onChange={e => setForm({ ...form, destinoId: e.target.value })}>
                                                        <option value="">Destino del Crédito</option>
                                                        {destinos.map(d => <option key={d.id} value={d.id}>{d.nombre}</option>)}
                                                    </select>
                                                </div>

                                                <div>
                                                    <label className="block text-[10px] uppercase font-bold text-gray-500 mb-1">Fecha Primer Vencimiento *</label>
                                                    <input
                                                        type="date"
                                                        className="w-full rounded-md border border-gray-300 p-2.5 text-sm bg-white focus:outline-none focus:border-[#0f8c79] focus:ring-1 focus:ring-[#0f8c79] text-gray-600"
                                                        value={form.fechaPrimerVencimiento || ''}
                                                        onChange={e => setForm({ ...form, fechaPrimerVencimiento: e.target.value })}
                                                    />
                                                </div>
                                            </div>

                                            {/* DETALLES DE LA REGLA SELECCIONADA - ROWS */}
                                            {regla && (
                                                <div className="mt-4 bg-gray-50/50 rounded-lg border border-gray-100 divide-y divide-gray-200/60 overflow-hidden">
                                                    <div className="flex justify-between items-center p-3 text-xs">
                                                        <span className="font-medium text-gray-500 uppercase tracking-wider">Tasa de Interés Nominal</span>
                                                        <span className="font-bold text-[#0f8c79]">{regla.tasaInteres}% Anual</span>
                                                    </div>
                                                    <div className="flex justify-between items-center p-3 text-xs">
                                                        <span className="font-medium text-gray-500 uppercase tracking-wider">Rango de Monto Permitido</span>
                                                        <span className="font-bold text-gray-700">Gs. {fmt(regla.montoMinimo)} - Gs. {fmt(regla.montoMaximo)}</span>
                                                    </div>
                                                    <div className="flex justify-between items-center p-3 text-xs">
                                                        <span className="font-medium text-gray-500 uppercase tracking-wider">Rango de Plazo Permitido</span>
                                                        <span className="font-bold text-gray-700">{regla.plazoMinimo} a {regla.plazoMaximo} Meses</span>
                                                    </div>
                                                </div>
                                            )}

                                            {/* SIMULACIÓN SIMPLE */}
                                            <div className="mt-8 border-t border-gray-200 pt-6">
                                                <div className="flex items-center justify-between bg-gray-50 p-6 rounded-xl border border-gray-200">
                                                    <div>
                                                        <p className="text-sm text-gray-500 mb-1">Cuota Mensual Estimada</p>
                                                        <p className="text-2xl font-bold text-[#0f8c79]">
                                                            Gs. {new Intl.NumberFormat('es-PY').format(simulacion?.[0]?.total || 0)}
                                                        </p>
                                                    </div>
                                                    <button onClick={handleSimular} className="px-6 py-2.5 border border-[#0f8c79] text-[#0f8c79] rounded-full text-sm font-bold hover:bg-teal-50 transition-colors">
                                                        Simular Cuota
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="pt-8 flex justify-between">
                                                <button onClick={() => setStep(1)} className="px-6 py-2.5 rounded-full text-sm font-bold text-gray-500 hover:text-gray-700 transition-colors">
                                                    Atrás
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        const porcentaje = viabilidadData?.porcentajeCoberturaAportes || 10;
                                                        const requerido = (form.monto * porcentaje) / 100;
                                                        const saldo = viabilidadData?.saldoAportes || 0;

                                                        if (saldo < requerido) {
                                                            Swal.fire('Requisito Insuficiente', `El socio debe poseer al menos el ${porcentaje}% del monto solicitado en sus aportes.`, 'warning');
                                                            return;
                                                        }
                                                        setStep(3);
                                                    }}
                                                    className={`px-8 py-2.5 rounded-full text-sm font-bold shadow-md transition-all ${(viabilidadData?.saldoAportes || 0) < ((form.monto * (viabilidadData?.porcentajeCoberturaAportes || 10)) / 100)
                                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                        : 'bg-[#0f8c79] text-white hover:bg-[#0c7363]'
                                                        }`}
                                                >
                                                    Siguiente
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* STEP 3: REFERENCIAS & CONTACTO */}
                                {step === 3 && socio && (
                                    <div className="space-y-10 animate-fade-in">
                                        <h3 className="text-xl font-bold text-[#2d4a5d] mb-8 border-b-2 border-[#0f8c79]/10 pb-2 flex items-center">
                                            <span className="bg-[#0f8c79] text-white w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm">3</span>
                                            Referencias y Contacto
                                        </h3>

                                        {/* DOMICILIO - ROW BASED */}
                                        <div className="space-y-6">
                                            <div className="border-b-2 border-[#0f8c79]/10 pb-2 mb-4">
                                                <p className="text-sm font-bold text-[#2d4a5d] uppercase tracking-wider flex items-center">
                                                    <svg className="w-4 h-4 mr-2 text-[#0f8c79]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>
                                                    Información Domiciliaria
                                                </p>
                                            </div>

                                            {address ? (
                                                <div className="divide-y divide-gray-100 bg-white rounded-lg border border-gray-100 shadow-sm overflow-hidden">
                                                    <div className="flex justify-between items-center px-6 py-4 bg-gray-50/50">
                                                        <span className="text-xs font-bold text-[#0f8c79] uppercase tracking-wide">Domicilio Principal Registrado</span>
                                                        <button onClick={handlePrepareUpdateAddress} className="text-[11px] font-bold text-gray-500 hover:text-[#0f8c79] transition-colors flex items-center">
                                                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                                                            Actualizar Dirección
                                                        </button>
                                                    </div>

                                                    <div className="flex items-center px-6 py-4 hover:bg-gray-50/30 transition-colors">
                                                        <span className="w-1/3 text-[11px] uppercase font-bold text-gray-400">Departamento</span>
                                                        <span className="w-2/3 text-sm font-bold text-gray-700">{address.nombreDepartamento || '-'}</span>
                                                    </div>

                                                    <div className="flex items-center px-6 py-4 hover:bg-gray-50/30 transition-colors">
                                                        <span className="w-1/3 text-[11px] uppercase font-bold text-gray-400">Ciudad</span>
                                                        <span className="w-2/3 text-sm font-bold text-gray-700">{address.nombreCiudad || '-'}</span>
                                                    </div>

                                                    <div className="flex items-center px-6 py-4 hover:bg-gray-50/30 transition-colors">
                                                        <span className="w-1/3 text-[11px] uppercase font-bold text-gray-400">Barrio</span>
                                                        <span className="w-2/3 text-sm font-bold text-gray-700">{address.nombreBarrio || '-'}</span>
                                                    </div>

                                                    <div className="flex items-center px-6 py-4 hover:bg-gray-50/30 transition-colors">
                                                        <span className="w-1/3 text-[11px] uppercase font-bold text-gray-400">Nro de Casa</span>
                                                        <span className="w-2/3 text-sm font-bold text-gray-700">{address.nroCasa || 'S/N'}</span>
                                                    </div>

                                                    <div className="flex flex-col px-6 py-4 gap-1 hover:bg-gray-50/30 transition-colors">
                                                        <span className="text-[11px] uppercase font-bold text-gray-400">Dirección Particular</span>
                                                        <span className="text-sm font-bold text-gray-800 leading-relaxed">{address.direccionExacta}</span>
                                                    </div>

                                                    <div className="flex flex-col px-6 py-4 gap-1 hover:bg-gray-50/30 transition-colors">
                                                        <span className="text-[11px] uppercase font-bold text-gray-400">Ubicación Exacta / Referencia</span>
                                                        <span className="text-sm font-medium text-gray-600 italic">{address.referencia || 'Sin referencia'}</span>
                                                    </div>
                                                </div>
                                            ) : (
                                                /* ... (Address Form Remains same as requested not to change inputs) ... */
                                                <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm space-y-5">
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                        <div>
                                                            <label className="block text-[10px] uppercase font-bold text-gray-500 mb-1.5">Departamento *</label>
                                                            <select
                                                                className="w-full rounded-lg border border-gray-300 p-2.5 text-sm bg-white focus:ring-2 focus:ring-[#0f8c79]/20 focus:border-[#0f8c79] transition-all outline-none"
                                                                value={addressForm.departamentoId}
                                                                onChange={e => setAddressForm({ ...addressForm, departamentoId: e.target.value })}
                                                            >
                                                                <option value="">Seleccionar Departamento</option>
                                                                {departamentos.map(d => <option key={d.id} value={d.id}>{d.nombre}</option>)}
                                                            </select>
                                                        </div>
                                                        <div>
                                                            <label className="block text-[10px] uppercase font-bold text-gray-500 mb-1.5">Ciudad *</label>
                                                            <select
                                                                className="w-full rounded-lg border border-gray-300 p-2.5 text-sm bg-white focus:ring-2 focus:ring-[#0f8c79]/20 focus:border-[#0f8c79] transition-all outline-none"
                                                                value={addressForm.ciudadId}
                                                                onChange={e => setAddressForm({ ...addressForm, ciudadId: e.target.value })}
                                                            >
                                                                <option value="">Seleccionar Ciudad</option>
                                                                {ciudades.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                                                            </select>
                                                        </div>
                                                        <div>
                                                            <label className="block text-[10px] uppercase font-bold text-gray-500 mb-1.5">Barrio *</label>
                                                            <select
                                                                className="w-full rounded-lg border border-gray-300 p-2.5 text-sm bg-white focus:ring-2 focus:ring-[#0f8c79]/20 focus:border-[#0f8c79] transition-all outline-none"
                                                                value={addressForm.barrioId}
                                                                onChange={e => setAddressForm({ ...addressForm, barrioId: e.target.value })}
                                                            >
                                                                <option value="">Seleccionar Barrio</option>
                                                                {barrios.map(b => <option key={b.id} value={b.id}>{b.nombre}</option>)}
                                                            </select>
                                                        </div>
                                                        <div>
                                                            <label className="block text-[10px] uppercase font-bold text-gray-500 mb-1.5">Nro Casa</label>
                                                            <input
                                                                className="w-full rounded-lg border border-gray-300 p-2.5 text-sm bg-white focus:ring-2 focus:ring-[#0f8c79]/20 focus:border-[#0f8c79] transition-all outline-none"
                                                                placeholder="Ej: 1234"
                                                                value={addressForm.nroCasa}
                                                                onChange={e => setAddressForm({ ...addressForm, nroCasa: e.target.value })}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <label className="block text-[10px] uppercase font-bold text-gray-500 mb-1.5">Dirección Particular *</label>
                                                        <input
                                                            className="w-full rounded-lg border border-gray-300 p-2.5 text-sm bg-white focus:ring-2 focus:ring-[#0f8c79]/20 focus:border-[#0f8c79] transition-all outline-none"
                                                            placeholder="Nombre de calle, nro de casa y entre calles"
                                                            value={addressForm.direccionExacta}
                                                            onChange={e => setAddressForm({ ...addressForm, direccionExacta: e.target.value })}
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-[10px] uppercase font-bold text-gray-500 mb-1.5">Ubicación Exacta / Referencia</label>
                                                        <input
                                                            className="w-full rounded-lg border border-gray-300 p-2.5 text-sm bg-white focus:ring-2 focus:ring-[#0f8c79]/20 focus:border-[#0f8c79] transition-all outline-none"
                                                            placeholder="Ej: A dos cuadras del supermercado X, casa portón verde"
                                                            value={addressForm.referencia}
                                                            onChange={e => setAddressForm({ ...addressForm, referencia: e.target.value })}
                                                        />
                                                    </div>
                                                    <button
                                                        onClick={handleAddressSave}
                                                        className="w-full bg-[#0f8c79] text-white py-3 rounded-lg text-sm font-bold hover:bg-[#0c7363] transition-all shadow-sm hover:shadow-md flex items-center justify-center group"
                                                    >
                                                        <svg className="w-4 h-4 mr-2 transition-transform group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                                                        Guardar y Validar Dirección
                                                    </button>
                                                </div>
                                            )}
                                        </div>

                                        {/* SERVICIOS DEL DOMICILIO (ANDE/ESSAP/COPACO) */}
                                        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                                            <SocioServiciosSection 
                                                servicios={servicios} 
                                                onChange={setServicios} 
                                            />
                                        </div>

                                        {/* CONTACTO - ROW BASED */}
                                        <div className="space-y-6">
                                            <div className="border-b-2 border-[#0f8c79]/10 pb-2 mb-4">
                                                <p className="text-sm font-bold text-[#2d4a5d] uppercase tracking-wider flex items-center">
                                                    <svg className="w-4 h-4 mr-2 text-[#0f8c79]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                                                    Canales de Contacto
                                                </p>
                                            </div>

                                            <div className="bg-white rounded-lg border border-gray-100 shadow-sm divide-y divide-gray-100">
                                                {/* FILA CELULAR 1 */}
                                                <div className="flex flex-col md:flex-row md:items-center px-6 py-5 gap-4">
                                                    <label className="w-full md:w-1/3 text-[11px] uppercase font-bold text-gray-400">Celular Principal</label>
                                                    <div className="w-full md:w-2/3 relative">
                                                        <span className="absolute left-3 top-2.5 text-gray-400 text-sm font-bold">+595</span>
                                                        <input
                                                            className="w-full rounded-lg border border-gray-300 p-2.5 pl-14 text-sm bg-gray-50/50 focus:ring-2 focus:ring-[#0f8c79]/20 focus:border-[#0f8c79] transition-all outline-none font-bold text-gray-700"
                                                            placeholder="09XX XXX XXX"
                                                            value={contactoForm.celular1.replace('+595', '').trim()}
                                                            onChange={e => setContactoForm({ ...contactoForm, celular1: e.target.value.startsWith('+595') ? e.target.value : '+595 ' + e.target.value })}
                                                        />
                                                    </div>
                                                </div>

                                                {/* FILA CELULAR 2 */}
                                                <div className="flex flex-col md:flex-row md:items-center px-6 py-5 gap-4">
                                                    <label className="w-full md:w-1/3 text-[11px] uppercase font-bold text-gray-400">Celular Secundario</label>
                                                    <div className="w-full md:w-2/3 relative">
                                                        <span className="absolute left-3 top-2.5 text-gray-400 text-sm font-bold">+595</span>
                                                        <input
                                                            className="w-full rounded-lg border border-gray-300 p-2.5 pl-14 text-sm bg-gray-50/50 focus:ring-2 focus:ring-[#0f8c79]/20 focus:border-[#0f8c79] transition-all outline-none font-medium text-gray-600"
                                                            placeholder="09XX XXX XXX"
                                                            value={contactoForm.celular2.replace('+595', '').trim()}
                                                            onChange={e => setContactoForm({ ...contactoForm, celular2: e.target.value.startsWith('+595') ? e.target.value : '+595 ' + e.target.value })}
                                                        />
                                                    </div>
                                                </div>

                                                {/* FILA EMAIL 1 */}
                                                <div className="flex flex-col md:flex-row md:items-center px-6 py-5 gap-4">
                                                    <label className="w-full md:w-1/3 text-[11px] uppercase font-bold text-gray-400">Email Personal</label>
                                                    <div className="w-full md:w-2/3">
                                                        <input
                                                            type="email"
                                                            className="w-full rounded-lg border border-gray-300 p-2.5 text-sm bg-gray-50/50 focus:ring-2 focus:ring-[#0f8c79]/20 focus:border-[#0f8c79] transition-all outline-none font-bold text-gray-700"
                                                            placeholder="correo@ejemplo.com"
                                                            value={contactoForm.email1}
                                                            onChange={e => setContactoForm({ ...contactoForm, email1: e.target.value })}
                                                        />
                                                    </div>
                                                </div>

                                                {/* FILA EMAIL 2 */}
                                                <div className="flex flex-col md:flex-row md:items-center px-6 py-5 gap-4">
                                                    <label className="w-full md:w-1/3 text-[11px] uppercase font-bold text-gray-400">Email Alternativo</label>
                                                    <div className="w-full md:w-2/3">
                                                        <input
                                                            type="email"
                                                            className="w-full rounded-lg border border-gray-300 p-2.5 text-sm bg-gray-50/50 focus:ring-2 focus:ring-[#0f8c79]/20 focus:border-[#0f8c79] transition-all outline-none font-medium text-gray-600"
                                                            placeholder="correo.laboral@ejemplo.com"
                                                            value={contactoForm.email2}
                                                            onChange={e => setContactoForm({ ...contactoForm, email2: e.target.value })}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* INFORMACIÓN LABORAL / PERFIL ECONÓMICO */}
                                        <div className="space-y-6">
                                            <div className="border-b-2 border-[#0f8c79]/10 pb-2 mb-4">
                                                <p className="text-sm font-bold text-[#2d4a5d] uppercase tracking-wider flex items-center">
                                                    <svg className="w-4 h-4 mr-2 text-[#0f8c79]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                                                    Perfil Laboral y Económico
                                                </p>
                                            </div>

                                            {/* SECCIÓN LABORAL GLOBAL */}
                                            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6">
                                                <p className="text-[10px] font-black text-[#0f8c79] uppercase tracking-widest mb-6 border-b border-gray-100 pb-2">Información Profesional Principal</p>
                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                                    <div className="flex flex-col gap-1.5">
                                                        <label className="text-[10px] uppercase font-bold text-gray-400">Profesión / Oficio</label>
                                                        <select
                                                            className="w-full rounded-lg border border-gray-300 p-2.5 text-sm bg-white focus:ring-2 focus:ring-[#0f8c79]/20 outline-none font-bold"
                                                            value={laboralForm.profesionId}
                                                            onChange={e => setLaboralForm({ ...laboralForm, profesionId: e.target.value })}
                                                        >
                                                            <option value="">Seleccione profesión...</option>
                                                            {profesiones.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                                                        </select>
                                                    </div>
                                                    <div className="flex flex-col gap-1.5">
                                                        <label className="text-[10px] uppercase font-bold text-gray-400">Rango de Antigüedad</label>
                                                        <select
                                                            className="w-full rounded-lg border border-gray-300 p-2.5 text-sm bg-white focus:ring-2 focus:ring-[#0f8c79]/20 outline-none font-bold"
                                                            value={laboralForm.rangoAntiguedadId}
                                                            onChange={e => setLaboralForm({ ...laboralForm, rangoAntiguedadId: e.target.value })}
                                                        >
                                                            <option value="">Seleccione rango...</option>
                                                            {rangosAntiguedad.map(r => <option key={r.id} value={r.id}>{r.descripcion}</option>)}
                                                        </select>
                                                    </div>
                                                    <div className="flex flex-col gap-1.5">
                                                        <label className="text-[10px] uppercase font-bold text-gray-400">Nombre de Empresa (si aplica)</label>
                                                        <input
                                                            className="w-full rounded-lg border border-gray-300 p-2.5 text-sm bg-white focus:ring-2 focus:ring-[#0f8c79]/20 outline-none font-bold"
                                                            placeholder="Lugar de trabajo principal"
                                                            value={laboralForm.empresa}
                                                            onChange={e => setLaboralForm({ ...laboralForm, empresa: e.target.value })}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* LISTADO DE INGRESOS */}
                                            <div className="space-y-8">
                                                {ingresos.map((ing, idx) => (
                                                    <div key={idx} className="relative group p-10 bg-slate-50/50 rounded-[2.5rem] border border-slate-200 shadow-xl shadow-slate-900/5 animate-fade-in hover:border-uni-teal/30 transition-all">
                                                        <div className="absolute -top-4 left-8 bg-slate-900 text-white px-6 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest shadow-xl">
                                                            Ingreso #{idx + 1}
                                                        </div>
                                                        
                                                        <button 
                                                            onClick={() => handleRemoveIngreso(idx)} 
                                                            className="absolute -top-3 -right-3 w-10 h-10 bg-rose-500 text-white rounded-xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all shadow-xl hover:scale-110 active:scale-90"
                                                        >
                                                            <Trash2 size={18} />
                                                        </button>
                                                        <div className="grid grid-cols-1 md:grid-cols-12 gap-x-8 gap-y-10 mt-4">
                                                            <div className="md:col-span-4">
                                                                <label className="text-[10px] uppercase font-black text-slate-400 mb-2 block ml-1">Origen / Modalidad</label>
                                                                <select
                                                                    className="w-full rounded-2xl border border-slate-200 p-3.5 text-sm bg-white focus:ring-4 focus:ring-uni-teal/5 focus:border-uni-teal transition-all outline-none font-bold text-slate-700"
                                                                    value={ing.tipoIngresoId}
                                                                    onChange={e => handleUpdateIngreso(idx, 'tipoIngresoId', e.target.value)}
                                                                >
                                                                    <option value="">Seleccione...</option>
                                                                    {tiposIngreso.map(t => <option key={t.id} value={t.id}>{t.nombre}</option>)}
                                                                </select>
                                                            </div>
                                                            <div className="md:col-span-4">
                                                                <label className="text-[10px] uppercase font-black text-slate-400 mb-2 block ml-1">Monto Neto (Gs.)</label>
                                                                <div className="relative">
                                                                    <span className="absolute left-4 top-3.5 text-emerald-500 font-black text-sm">Gs.</span>
                                                                    <input
                                                                        type="number"
                                                                        className="w-full rounded-2xl border border-slate-200 p-3.5 pl-12 text-lg font-black bg-white focus:ring-4 focus:ring-uni-teal/5 focus:border-uni-teal transition-all outline-none text-slate-800"
                                                                        value={ing.montoMensualPromedio}
                                                                        onChange={e => handleUpdateIngreso(idx, 'montoMensualPromedio', e.target.value)}
                                                                    />
                                                                </div>
                                                            </div>
                                                            <div className="md:col-span-4">
                                                                <label className="text-[10px] uppercase font-black text-slate-400 mb-2 block ml-1">Promedio Últ. 6 Meses</label>
                                                                <input
                                                                    type="text"
                                                                    className="w-full rounded-2xl border border-slate-200 p-3.5 text-sm bg-white focus:ring-4 focus:ring-uni-teal/5 focus:border-uni-teal transition-all outline-none font-bold"
                                                                    placeholder="Monto promedio"
                                                                    value={ing.promedioUltimos6Meses}
                                                                    onChange={e => handleUpdateIngreso(idx, 'promedioUltimos6Meses', e.target.value)}
                                                                />
                                                            </div>

                                                            <div className="md:col-span-6">
                                                                <label className="text-[10px] uppercase font-black text-slate-400 mb-2 block ml-1">Empresa / Pagador</label>
                                                                <input
                                                                    className="w-full rounded-2xl border border-slate-200 p-3.5 text-sm bg-white focus:ring-4 focus:ring-uni-teal/5 focus:border-uni-teal transition-all outline-none font-bold"
                                                                    placeholder="Nombre de la empresa o fuente"
                                                                    value={ing.empresa}
                                                                    onChange={e => handleUpdateIngreso(idx, 'empresa', e.target.value)}
                                                                />
                                                            </div>
                                                            <div className="md:col-span-6">
                                                                <label className="text-[10px] uppercase font-black text-slate-400 mb-2 block ml-1">Dirección Laboral</label>
                                                                <input
                                                                    className="w-full rounded-2xl border border-slate-200 p-3.5 text-sm bg-white focus:ring-4 focus:ring-uni-teal/5 focus:border-uni-teal transition-all outline-none font-bold"
                                                                    placeholder="Ubicación de la actividad"
                                                                    value={ing.direccion}
                                                                    onChange={e => handleUpdateIngreso(idx, 'direccion', e.target.value)}
                                                                />
                                                            </div>

                                                            <div className="md:col-span-12">
                                                                <label className="text-[10px] uppercase font-black text-slate-400 mb-2 block ml-1">Observaciones / Descripción Fuente</label>
                                                                <input
                                                                    className="w-full rounded-2xl border border-slate-200 p-3.5 text-sm bg-white focus:ring-4 focus:ring-uni-teal/5 focus:border-uni-teal transition-all outline-none font-medium"
                                                                    placeholder="Detalle adicional sobre el origen de los fondos..."
                                                                    value={ing.descripcionFuente}
                                                                    onChange={e => handleUpdateIngreso(idx, 'descripcionFuente', e.target.value)}
                                                                />
                                                            </div>

                                                            <div className="md:col-span-12 flex items-center gap-6 p-4 bg-white rounded-2xl border border-slate-100">
                                                                <label className="flex items-center gap-3 cursor-pointer group/check">
                                                                    <input
                                                                        type="checkbox"
                                                                        className="w-5 h-5 rounded border-slate-300 text-uni-teal focus:ring-uni-teal transition-all"
                                                                        checked={ing.verificadoPortal}
                                                                        onChange={e => handleUpdateIngreso(idx, 'verificadoPortal', e.target.checked)}
                                                                    />
                                                                    <span className="text-[10px] font-black uppercase text-slate-500 group-hover/check:text-uni-teal transition-colors">¿Verificado en Portal / IPS?</span>
                                                                </label>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                                
                                                <div className="flex justify-center pt-8">
                                                    <button
                                                        type="button"
                                                        onClick={handleAddIngreso}
                                                        className="flex items-center gap-3 px-10 py-4 bg-slate-900 text-white rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-uni-teal transition-all shadow-2xl hover:scale-105 active:scale-95"
                                                    >
                                                        <Plus className="w-5 h-5" /> Agregar Otra Fuente de Ingreso
                                                    </button>
                                                </div>
                                            </div>

                                                <div className="p-8 bg-slate-50/80">
                                                    <div className="flex flex-col md:flex-row md:items-center gap-8">
                                                        <div className="flex-1">
                                                            <div className="flex items-center gap-3 mb-2">
                                                                <div className="w-8 h-8 rounded-lg bg-[#0f8c79]/10 flex items-center justify-center text-[#0f8c79]">
                                                                    <Upload size={16} />
                                                                </div>
                                                                <label className="block text-xs font-black text-slate-700 uppercase tracking-wider">Comprobante de Ingresos</label>
                                                            </div>
                                                            <p className="text-[10px] text-slate-500 font-medium leading-relaxed">
                                                                Adjunte la última liquidación de salario, certificado de trabajo o formularios de IVA del último trimestre.
                                                                Formatos aceptados: <span className="font-bold text-slate-700">PDF, JPG, PNG</span>.
                                                            </p>
                                                        </div>
                                                        <div className="flex-1">
                                                            <div className="relative group">
                                                                <input
                                                                    type="file"
                                                                    onChange={e => setFileIngreso(e.target.files[0])}
                                                                    className="hidden"
                                                                    id="file-ingreso-input"
                                                                    accept="image/*,.pdf"
                                                                />
                                                                <label
                                                                    htmlFor="file-ingreso-input"
                                                                    className={`flex flex-col items-center justify-center p-6 rounded-2xl border-2 border-dashed cursor-pointer transition-all duration-300 
                                                                ${fileIngreso
                                                                            ? 'border-emerald-500 bg-emerald-50 text-emerald-700 shadow-inner'
                                                                            : 'border-slate-300 bg-white group-hover:border-[#0f8c79] group-hover:bg-slate-50 text-slate-400'}`}
                                                                >
                                                                    {fileIngreso ? (
                                                                        <>
                                                                            <CheckCircle className="text-emerald-500 mb-2" size={24} />
                                                                            <span className="text-xs font-black truncate max-w-[200px]">{fileIngreso.name}</span>
                                                                            <span className="text-[9px] uppercase mt-1 opacity-60">Click para cambiar</span>
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <Upload className="mb-2 group-hover:bounce" size={24} />
                                                                            <span className="text-xs font-black uppercase tracking-widest">Subir Documento</span>
                                                                            <span className="text-[9px] mt-1">Máximo 5MB</span>
                                                                        </>
                                                                    )}
                                                                </label>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="p-4 bg-slate-100/50 flex justify-end border-t border-gray-200">
                                                    <button
                                                        onClick={handleUpdateSocioProfile}
                                                        className="flex items-center gap-2 px-6 py-2.5 bg-white border-2 border-[#0f8c79] text-[#0f8c79] hover:bg-[#0f8c79] hover:text-white rounded-xl font-black text-xs uppercase tracking-widest transition-all duration-300 shadow-sm hover:shadow-teal-100"
                                                    >
                                                        <Save size={14} />
                                                        Actualizar Perfil del Socio
                                                    </button>
                                                </div>
                                            </div>
                                        
                                        {/* REFERENCIAS PERSONALES FIJAS */}
                                        <div className="space-y-6 pt-6">
                                            <p className="text-sm font-bold text-gray-700 border-b border-gray-200 pb-2">Referencias Personales</p>

                                            {/* Referencia 1 */}
                                            <div className="space-y-2">
                                                <p className="text-xs font-bold text-[#0f8c79] uppercase">Referencia Personal 1</p>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    <div>
                                                        <label className="block text-[10px] uppercase font-bold text-gray-500 mb-1">Nombre Completo</label>
                                                        <input
                                                            className="w-full rounded-md border border-gray-300 p-2.5 text-sm bg-white focus:outline-none focus:border-[#0f8c79]"
                                                            placeholder="Nombre y Apellido"
                                                            value={referencias[0]?.nombre || ''}
                                                            onChange={e => {
                                                                const newRefs = [...referencias];
                                                                if (!newRefs[0]) newRefs[0] = { nombre: '', telefono: '' };
                                                                newRefs[0].nombre = e.target.value;
                                                                setReferencias(newRefs);
                                                            }}
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-[10px] uppercase font-bold text-gray-500 mb-1">Teléfono</label>
                                                        <input
                                                            className="w-full rounded-md border border-gray-300 p-2.5 text-sm bg-white focus:outline-none focus:border-[#0f8c79]"
                                                            placeholder="Ej: 0981 123 456"
                                                            value={referencias[0]?.telefono || ''}
                                                            onChange={e => {
                                                                const newRefs = [...referencias];
                                                                if (!newRefs[0]) newRefs[0] = { nombre: '', telefono: '' };
                                                                newRefs[0].telefono = e.target.value;
                                                                setReferencias(newRefs);
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Referencia 2 */}
                                            <div className="space-y-2">
                                                <p className="text-xs font-bold text-[#0f8c79] uppercase">Referencia Personal 2</p>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    <div>
                                                        <label className="block text-[10px] uppercase font-bold text-gray-500 mb-1">Nombre Completo</label>
                                                        <input
                                                            className="w-full rounded-md border border-gray-300 p-2.5 text-sm bg-white focus:outline-none focus:border-[#0f8c79]"
                                                            placeholder="Nombre y Apellido"
                                                            value={referencias[1]?.nombre || ''}
                                                            onChange={e => {
                                                                const newRefs = [...referencias];
                                                                if (!newRefs[1]) newRefs[1] = { nombre: '', telefono: '' };
                                                                newRefs[1].nombre = e.target.value;
                                                                setReferencias(newRefs);
                                                            }}
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-[10px] uppercase font-bold text-gray-500 mb-1">Teléfono</label>
                                                        <input
                                                            className="w-full rounded-md border border-gray-300 p-2.5 text-sm bg-white focus:outline-none focus:border-[#0f8c79]"
                                                            placeholder="Ej: 0981 123 456"
                                                            value={referencias[1]?.telefono || ''}
                                                            onChange={e => {
                                                                const newRefs = [...referencias];
                                                                if (!newRefs[1]) newRefs[1] = { nombre: '', telefono: '' };
                                                                newRefs[1].telefono = e.target.value;
                                                                setReferencias(newRefs);
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="pt-8 mt-8 border-t border-gray-200 flex justify-between">
                                            <button onClick={() => setStep(2)} className="px-6 py-2.5 rounded-full text-sm font-bold text-gray-500 hover:text-gray-700 transition-colors">Atrás</button>
                                            <button onClick={() => setStep(4)} className="px-8 py-2.5 rounded-full text-sm font-bold bg-[#0f8c79] text-white hover:bg-[#0c7363] shadow-md transition-all">Siguiente</button>
                                        </div>
                                    </div>
                                )}

                                {/* STEP 4: CIERRE DOCUMENTAL */}
                                {step === 4 && socio && (
                                    <div className="space-y-8 animate-fade-in">
                                        <h3 className="text-lg font-bold text-[#2d4a5d] mb-6">Verificación de Documentos Físicos</h3>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {[
                                                { key: 'tieneCedula', label: 'Cédula de Identidad' },
                                                { key: 'tieneLiquidacion', label: 'Liquidación de Salario' },
                                                { key: 'tieneCertificadoTrabajo', label: 'Certificado de Trabajo' },
                                                { key: 'tieneFacturaServicio', label: 'Factura ANDE/ESSAP' }
                                            ].map(doc => (
                                                <label key={doc.key} className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-md cursor-pointer hover:border-[#0f8c79] transition-colors">
                                                    <span className="text-sm font-bold text-gray-700">{doc.label}</span>
                                                    <input
                                                        type="checkbox"
                                                        checked={legajo[doc.key]}
                                                        onChange={e => setLegajo({ ...legajo, [doc.key]: e.target.checked })}
                                                        className="w-5 h-5 text-[#0f8c79] border-gray-300 rounded focus:ring-[#0f8c79]"
                                                    />
                                                </label>
                                            ))}
                                        </div>

                                        {legajo.tieneLiquidacion && (
                                            <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-md">
                                                <p className="text-sm font-bold text-gray-700 mb-2">Monto Ingreso Real según Liquidación *</p>
                                                <input
                                                    type="number"
                                                    className="w-full md:w-1/3 rounded-md border border-gray-300 p-2.5 text-sm bg-white focus:outline-none focus:border-[#0f8c79]"
                                                    value={legajo.montoIngresoReal}
                                                    onChange={e => setLegajo({ ...legajo, montoIngresoReal: e.target.value })}
                                                />
                                            </div>
                                        )}

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8 border-t border-gray-200 pt-8">
                                            <div>
                                                <p className="text-sm font-bold text-gray-700 mb-4">Generación de Kit Legal</p>
                                                <div className="space-y-2">
                                                    {["Pagaré a la Orden", "Autorización Informconf", "Contrato de Préstamo", "Liquidación de Desembolso"].map((doc, idx) => (
                                                        <div key={idx} className="flex justify-between items-center p-3 bg-gray-50 border border-gray-200 rounded-md">
                                                            <span className="text-sm text-gray-700">{doc}</span>
                                                            <span className="text-xs font-bold text-[#0f8c79]">Listo</span>
                                                        </div>
                                                    ))}
                                                    <button onClick={handlePrint} className="w-full mt-4 bg-gray-800 text-white py-2.5 rounded-md text-sm font-bold hover:bg-gray-700">
                                                        Imprimir PDF Completo
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="bg-[#0f8c79] rounded-xl p-8 text-white flex flex-col justify-center text-center shadow-lg">
                                                <h4 className="text-xl font-bold mb-2">¡Todo Listo!</h4>
                                                <p className="text-sm text-teal-100 mb-8">La solicitud está completa y validada.</p>
                                                <button onClick={handleFinalSubmit} className="bg-white text-[#0f8c79] py-3 rounded-full font-bold text-sm hover:bg-teal-50 transition-colors">
                                                    Finalizar Solicitud
                                                </button>
                                            </div>
                                        </div>

                                        {/* HIDDEN PRINT CONTAINER (Keep for handlePrint) */}
                                        <div className="hidden print-container">
                                            <div className="page-break" dangerouslySetInnerHTML={{ __html: renderFullPagare() }} />
                                            <div className="page-break" dangerouslySetInnerHTML={{ __html: renderFullInformconf() }} />
                                            <div className="page-break" dangerouslySetInnerHTML={{ __html: renderFullContrato() }} />
                                            <div className="page-break" dangerouslySetInnerHTML={{ __html: renderFullLiquidation() }} />
                                        </div>

                                        <div className="pt-8 mt-8 border-t border-gray-200 flex justify-start">
                                            <button onClick={() => setStep(3)} className="px-6 py-2.5 rounded-full text-sm font-bold text-gray-500 hover:text-gray-700 transition-colors">Atrás</button>
                                        </div>
                                    </div>
                                )}
                            </>
                        </div>
                    </div>
                </div>
                {/* SIMULATION MODAL */}
                {showSimulateModal && simulacion && (
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                        <div className="bg-white rounded-3xl shadow-2xl border border-teal-600 max-w-4xl w-full max-h-[85vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
                            {/* Modal Header */}
                            <div className="bg-[#2d4a5d] p-5 flex justify-between items-center text-white">
                                <div>
                                    <h3 className="text-lg font-bold">Amortización Estimada</h3>
                                    <p className="text-[11px] text-teal-200 font-bold uppercase tracking-wider mt-0.5">Detalle de Cuotas Generadas - Sistema Francés</p>
                                </div>
                                <button 
                                    onClick={() => setShowSimulateModal(false)}
                                    className="p-1.5 hover:bg-white/10 rounded-full transition-colors text-white"
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                                </button>
                            </div>

                            {/* Modal Body */}
                            <div className="p-6 overflow-y-auto flex-1 bg-slate-50">
                                {/* Summary Cards */}
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                                    <div className="bg-white p-3 border border-slate-200 rounded-xl shadow-sm text-center">
                                        <span className="text-[9px] uppercase font-bold text-slate-400 block mb-0.5">Monto Solicitado</span>
                                        <span className="text-sm font-black text-slate-800">Gs. {new Intl.NumberFormat('es-PY').format(form.monto)}</span>
                                    </div>
                                    <div className="bg-white p-3 border border-slate-200 rounded-xl shadow-sm text-center">
                                        <span className="text-[9px] uppercase font-bold text-slate-400 block mb-0.5">Plazo Asignado</span>
                                        <span className="text-sm font-black text-slate-800">{form.plazo} Meses</span>
                                    </div>
                                    <div className="bg-white p-3 border border-slate-200 rounded-xl shadow-sm text-center">
                                        <span className="text-[9px] uppercase font-bold text-slate-400 block mb-0.5">Tasa Aplicada (TNA)</span>
                                        <span className="text-sm font-black text-slate-800">{regla?.tasaCompensatoria || regla?.tasaInteres || 0}% Anual</span>
                                    </div>
                                    <div className="bg-white p-3 border border-slate-200 rounded-xl shadow-sm text-center">
                                        <span className="text-[9px] uppercase font-bold text-slate-400 block mb-0.5">Cuota Mensual</span>
                                        <span className="text-sm font-black text-teal-600">Gs. {new Intl.NumberFormat('es-PY').format(simulacion[0]?.total || 0)}</span>
                                    </div>
                                </div>

                                {/* Installments Table */}
                                <div className="overflow-x-auto border border-slate-200 rounded-2xl bg-white shadow-sm">
                                    <table className="min-w-full text-left border-collapse text-xs">
                                        <thead>
                                            <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 font-bold uppercase tracking-wider">
                                                <th className="px-4 py-3 text-center">Nº de Cuota</th>
                                                <th className="px-4 py-3">Vencimiento</th>
                                                <th className="px-4 py-3 text-right">Capital al inicio del período</th>
                                                <th className="px-4 py-3 text-right">Amortización</th>
                                                <th className="px-4 py-3 text-right">Intereses</th>
                                                <th className="px-4 py-3 text-right">Cuota</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100 font-medium text-slate-700">
                                            {((simulacion || []).slice((modalPage - 1) * 10, modalPage * 10)).map((cuota, idx) => {
                                                const absoluteIdx = (modalPage - 1) * 10 + idx;
                                                const capitalInicio = absoluteIdx === 0 ? form.monto : (simulacion[absoluteIdx - 1]?.saldo || 0);
                                                const formatCurrency = (val) => {
                                                    return '$' + new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val || 0);
                                                };
                                                const formatVencimiento = (venc) => {
                                                    if (!venc) return '';
                                                    if (Array.isArray(venc)) {
                                                        const [year, month, day] = venc;
                                                        return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
                                                    }
                                                    return venc;
                                                };
                                                return (
                                                    <tr key={cuota.numeroCuota} className="hover:bg-slate-50/50 transition-colors">
                                                        <td className="px-4 py-2.5 text-center font-bold text-slate-500">{cuota.numeroCuota}</td>
                                                        <td className="px-4 py-2.5">{formatVencimiento(cuota.vencimiento)}</td>
                                                        <td className="px-4 py-2.5 text-right font-mono">{formatCurrency(capitalInicio)}</td>
                                                        <td className="px-4 py-2.5 text-right font-mono">{formatCurrency(cuota.capital || 0)}</td>
                                                        <td className="px-4 py-2.5 text-right font-mono">{formatCurrency(cuota.interes || 0)}</td>
                                                        <td className="px-4 py-2.5 text-right font-black text-slate-900 font-mono">{formatCurrency(cuota.total || 0)}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* Modal Footer / Pagination */}
                            <div className="bg-slate-50 p-4 border-t border-slate-200 flex justify-between items-center text-xs font-bold text-slate-500">
                                <div>
                                    Mostrando cuotas <span className="text-slate-800">{Math.min((modalPage - 1) * 10 + 1, simulacion.length)}</span> - <span className="text-slate-800">{Math.min(modalPage * 10, simulacion.length)}</span> de <span className="text-slate-800">{simulacion.length}</span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <button 
                                        type="button"
                                        disabled={modalPage === 1}
                                        onClick={() => setModalPage(modalPage - 1)}
                                        className="px-3 py-1.5 border border-slate-300 bg-white hover:bg-slate-100 disabled:opacity-30 rounded-lg text-[11px] transition-colors"
                                    >
                                        Anterior
                                    </button>
                                    <div className="px-3 py-1.5 bg-[#0f8c79] text-white rounded-lg">
                                        {modalPage} de {Math.ceil(simulacion.length / 10)}
                                    </div>
                                    <button 
                                        type="button"
                                        disabled={modalPage >= Math.ceil(simulacion.length / 10)}
                                        onClick={() => setModalPage(modalPage + 1)}
                                        className="px-3 py-1.5 border border-slate-300 bg-white hover:bg-slate-100 disabled:opacity-30 rounded-lg text-[11px] transition-colors"
                                    >
                                        Siguiente
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                <StyleTag />
            </div>
        </div>
    );
};

const styles = `
@media print {
    body * {
        visibility: hidden;
    }
    .print-container, .print-container * {
        visibility: visible;
    }
    .print-container {
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
    }
    .no-print {
        display: none !important;
    }
    .page-break {
        page-break-after: always;
        border: none !important;
        box-shadow: none !important;
        margin: 0 !important;
        padding: 0 !important;
    }
    @page {
        size: A4;
        margin: 0;
    }
}

/* Enterprise Layout Fixes */
.enterprise-field {
    position: relative;
    margin-bottom: 0.5rem;
}
.enterprise-field label {
    position: absolute;
    top: -6px;
    left: 12px;
    background: white;
    padding: 0 4px;
    font-size: 9px;
    font-weight: 900;
    text-transform: uppercase;
    color: #94a3b8;
    letter-spacing: 0.05em;
    pointer-events: none;
}
.enterprise-input {
    width: 100%;
    padding: 10px 14px;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    font-size: 13px;
    font-weight: 600;
    color: #1e293b;
    transition: all 0.2s;
    background: #f8fafc;
}
.enterprise-input:focus {
    outline: none;
    border-color: #3b82f6;
    background: white;
    box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
}

`;

const StyleTag = () => <style dangerouslySetInnerHTML={{ __html: styles }} />;

export default SolicitudCredito;
